import { useMemo, useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { LoadingState } from "@/components/base/LoadingState";
import { MoneyInput } from "@/components/base/MoneyInput";
import VariantMatrixEditor, { type VariationPayload } from "@/pages/products/create/components/VariantMatrixEditor";
import SupplierSection, { emptySupplierSelection, type SupplierSelection } from "@/pages/products/create/components/SupplierSection";
import { uploadDraftMediaAsset, signMediaUrls, removePrivateMediaObjects, validateFile } from "@/lib/media";
import * as productApi from "@/lib/api/products";
import { linkSupplierProduct } from "@/lib/api/supplier-products";
import { flattenWithDepth, findDuplicateCategory, type Category } from "@/lib/categories";
import { enrichProduct, enrichmentDraftCaption, type EnrichmentDraft } from "@/lib/enrichment";
import SuccessScreen, { type CreatedResult } from "@/pages/products/create/components/SuccessScreen";
import WizardSellingStep from "@/pages/products/create/components/WizardSellingStep";
import { wizardDraftIsMeaningful, wizardDraftTitle, shouldAutosaveWizardDraft, type WizardDraftSaveStatus } from "@/lib/wizard-draft";
import { discardProductWizardDraft, loadProductWizardDraft, upsertProductWizardDraft, attachWizardDraftMedia, detachWizardDraftMedia, transferWizardDraftMedia, loadWizardDraftMedia } from "@/lib/api/product-wizard-drafts";
import { formatMoneyPreview } from "@/lib/money-entry";
import { canonicalSkuBase, findCanonicalSkuCollisions, canonicalSkuList, normalizeSku, skuValidationError, validateIdentifierValue } from "@/lib/products";
import { matrixLocationProblem, openingStockRowProblems, openingStockTotal, projectOpeningStock, resolveMatrixLocationId, visibleOpeningQuantityError } from "@/lib/opening-stock";
import { recordWizardChannelDrafts } from "@/lib/wizard-channel-drafts";
import {
  applyWizardChannelPrimaryMedia,
  pruneWizardChannelPrimaryMedia,
  resolveWizardChannelPrimaryMedia,
  wizardChannelMediaApplyPlan,
  type WizardMediaOption,
} from "@/lib/wizard-channel-media";
import { abandonCreateAttempt, attemptEpochIsCurrent, bindCreatedProduct, canDiscardDraft, commitAttemptResult, failSubmission, finishSubmission, nextAttemptEpoch, pauseAfterCreate, runGuardedRetry, successIdentity, type AttemptEpoch, type RetryGate, type SubmissionLock } from "@/lib/create-submission-lock";
import {
  browserRecoveryStorage,
  canRebuildAttemptFromDraft,
  classifyCreateFailure,
  clearCreateRecoveryOwned,
  confirmCreateRecoveryPersisted,
  decideCreateReconciliation,
  draftExitState,
  evaluateCreateEligibility,
  hasUnfinishedPostCreate,
  readCreateRecoveryState,
  recoveryHoldMessage,
  recoveryHoldStep,
  recoveryNoticeMessage,
  recoveryRequiresKeyReuse,
  resolveCanonicalCreateKey,
  shouldDiscardDraftAfterCreate,
  writePendingCreateRecovery,
  writeResolvedCreateRecovery,
} from "@/lib/wizard-create-recovery";
import { captureDraftMedia, captureDraftSave, draftMediaStillCurrent, draftSaveStillCurrent, type DraftOwner } from "@/lib/wizard-draft-ownership";
import { draftMediaRemovalFailure, draftMediaRemovalWarning, reconcileDraftMediaRemoval } from "@/lib/wizard-draft-media-removal";
import { draftTransferFailureId, resolveDraftTransferFailures, unresolvedMediaFailureCount, upsertMediaFailure, type WizardMediaFailure } from "@/lib/wizard-media-failures";
import { createOrderedDraftPersist, type DraftPersistWriteResult } from "@/lib/wizard-draft-persist";
import { resolveWizardDraftOwnership } from "@/lib/wizard-draft-identity";
import { blankCreateDraftState, hydrateCreateDraftState } from "@/lib/wizard-draft-hydration";
import { displayedUrlSlug } from "@/lib/wizard-seo";
import { enrichmentNeedsOverwriteConfirm, mergeEnrichmentFields } from "@/lib/ai-field-merge";
import { isCanonicalCreateRequired } from "@/lib/wizard-fields";
import {
  snapshotFromWizard,
} from "@/lib/marketplace-readiness";
import {
  CANONICAL_CONDITION_OPTIONS,
  evaluateProductDetailsPreparation,
  formatAmazonCatalogueStatus,
  sanitizeManufacturerIdentifier,
} from "@/lib/catalogue-facts";
import {
  captureWizardSellingIntents,
  evaluateWizardSelling,
  mergeWizardSellingIntents,
  resolveWizardStepIndex,
  selectedReadyCount,
  shouldMergeWizardSellingIntents,
  uniqueRecommendedFixes,
  uniqueRequiredFixes,
  wizardCanLeaveStep,
  wizardConnectorSaveDecision,
  wizardDraftListingCategory,
  wizardListingConfigSummary,
  wizardSellingLeavePayload,
  type WizardChannelIntent,
  WIZARD_STEP_KEYS,
} from "@/lib/wizard-selling";
import {
  classifyDraftMediaTransfer,
  classifyExistingListing,
  derivePostCreateObligations,
  incompletePostCreateStage,
  mergeChannelOutcomes,
  planChannelSteps,
  type ChannelOutcome,
} from "@/lib/wizard-post-create";
import { createChannelListing } from "@/lib/channels";
import { useConnectorRowSubscription } from "@/hooks/useConnectorRowSubscription";
import { formatWizardPriceRange, resolveWizardChannelPrice, wizardListingPricePayload, wizardPriceAmountError, wizardQuotePriceError } from "@/lib/wizard-channel-price";

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

type ProductType = "simple" | "variation" | "bundle";

type CategoryRow = Category;
interface SupplierRow { id: string; name: string; }
interface LocationRow { id: string; name: string; }

interface FormState {
  productType: ProductType;
  name: string;
  reference: string;
  brand: string;
  categoryId: string;
  status: "active" | "draft";
  description: string;
  shortDescription: string;
  seoTitle: string;
  metaDescription: string;
  seoKeywords: string;
  tags: string;
  unit: string;
  sku: string;
  barcode: string;
  costPrice: string;
  sellingPrice: string;
  taxProfile: string;
  weightKg: string;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
  manufacturer: string;
  modelNumber: string;
  mpn: string;
  condition: string;
  countryOfOrigin: string;
  material: string;
  gtin: string;
  upc: string;
  ean: string;
  identifiersNotApplicable: boolean;
  urlSlug: string;
  slugEdited: boolean;
  reorderPoint: string;
  reorderQty: string;
  supplier: SupplierSelection;
}

const initialForm: FormState = {
  productType: "simple",
  name: "",
  reference: "",
  brand: "",
  categoryId: "",
  status: "active",
  description: "",
  shortDescription: "",
  seoTitle: "",
  metaDescription: "",
  seoKeywords: "",
  tags: "",
  unit: "each",
  sku: "",
  barcode: "",
  costPrice: "",
  sellingPrice: "",
  taxProfile: "",
  weightKg: "",
  lengthCm: "",
  widthCm: "",
  heightCm: "",
  manufacturer: "",
  modelNumber: "",
  mpn: "",
  condition: "",
  countryOfOrigin: "",
  material: "",
  gtin: "",
  upc: "",
  ean: "",
  identifiersNotApplicable: false,
  urlSlug: "",
  slugEdited: false,
  reorderPoint: "0",
  reorderQty: "0",
  supplier: emptySupplierSelection,
};

interface DraftMediaView {
  id: string;
  mediaAssetId: string;
  storageKey: string;
  filename: string;
  position: number;
  isPrimary: boolean;
  src: string | null;
}

const num = (v: string): number | null => (v.trim() === "" ? null : Number(v));

interface WizardDraftPersistSnap {
  orgId: string | undefined;
  draftId: string | null;
  form: FormState;
  variation: VariationPayload;
  openingStock: { location_id: string; qty: string; unit_cost: string; reference: string; variant_sku: string }[];
  stockChoice: "none" | "record";
  simpleQty: string;
  matrixLocationId: string;
  stockByLocation: boolean;
  step: number;
  created: boolean;
  hydrationReady: boolean;
  stepKey: string;
  mediaCount: number;
  sellingIntents: WizardChannelIntent[];
}

function cloneWizardDraftSnap(snap: WizardDraftPersistSnap): WizardDraftPersistSnap {
  return {
    ...snap,
    form: { ...snap.form, supplier: { ...snap.form.supplier } },
    variation: {
      ...snap.variation,
      options: [...snap.variation.options],
      variants: snap.variation.variants.map((variant) => ({ ...variant })),
      duplicateSkus: [...snap.variation.duplicateSkus],
    },
    openingStock: snap.openingStock.map((row) => ({ ...row })),
    sellingIntents: captureWizardSellingIntents(snap.sellingIntents),
  };
}

interface CreateAttemptSnapshot {
  orgId: string;
  draftId: string | null;
  form: FormState;
  variation: VariationPayload;
  openingStock: { location_id: string; qty: string; unit_cost: string; reference: string; variant_sku: string }[];
  stockChoice: "none" | "record";
  simpleQty: string;
  defaultLocationId: string | null;
  locations: { id: string; name: string }[];
  sellingIntents: WizardChannelIntent[];
  hasMedia: boolean;
  /** Media assets that will exist in the product gallery after the draft transfer. */
  mediaAssetIds: string[];
}

/** Derive a canonical multichannel-safe SKU from a product name. */
function suggestSku(name: string): string {
  return canonicalSkuBase(name);
}

/**
 * Final pre-submission gate for the four-variant launch journey.
 *
 * Everything here is knowable before an identity is written, so a refusal must
 * happen before the write-ahead record: a create that was never attempted must
 * not leave a recovery record behind. It also closes the case where entered
 * matrix quantities are silently dropped for want of a stock location.
 */
function submissionValidationError(attempt: CreateAttemptSnapshot): string | null {
  if (!attempt.form.name.trim()) return "Enter a product name before creating it.";
  if (attempt.form.productType === "variation") {
    if (attempt.variation.variants.length === 0) {
      return "Add at least one variant combination before creating this product.";
    }
    const invalidSku = attempt.variation.variants.find((variant) => skuValidationError(variant.sku) !== null);
    if (invalidSku) {
      return `${skuValidationError(invalidSku.sku)} Variant: ${invalidSku.sku.trim() || "unnamed"}.`;
    }
    const collisions = findCanonicalSkuCollisions(attempt.variation.variants.map((variant) => variant.sku));
    if (collisions.length > 0) {
      return `These variants share one canonical SKU and cannot be created: ${collisions
        .map((row) => `${row.sku} conflicts with ${row.conflictsWith}`)
        .join("; ")}.`;
    }
  } else {
    if (!attempt.form.sku.trim()) return "A SKU is required before creating this product.";
    const skuError = skuValidationError(attempt.form.sku);
    if (skuError) return skuError;
  }
  const matrix = matrixLocationProblem({
    productType: attempt.form.productType,
    defaultLocationId: attempt.defaultLocationId,
    variants: attempt.variation.variants,
  });
  if (matrix) return matrix;
  const stockProblems = openingStockRowProblems({
    productType: attempt.form.productType,
    defaultLocationId: attempt.defaultLocationId,
    stockChoice: attempt.stockChoice,
    simpleQty: attempt.simpleQty,
    productCost: num(attempt.form.costPrice),
    variants: attempt.variation.variants,
    locationRows: attempt.openingStock,
  });
  if (stockProblems.length > 0) return stockProblems[0];
  return null;
}

export default function CreateProduct() {
  const { org } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useConnectorRowSubscription(org?.id);
  const [searchParams, setSearchParams] = useSearchParams();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(initialForm);
  const [variation, setVariation] = useState<VariationPayload>({ options: [], variants: [], combinationCount: 0, duplicateSkus: [], hasData: false });
  const [draftMedia, setDraftMedia] = useState<DraftMediaView[]>([]);
  const [mediaBusy, setMediaBusy] = useState(false);
  const [openingStock, setOpeningStock] = useState<{ location_id: string; qty: string; unit_cost: string; reference: string; variant_sku: string }[]>([]);
  const [stockChoice, setStockChoice] = useState<"none" | "record">("none");
  const [saving, setSaving] = useState(false);
  const [discardingDraft, setDiscardingDraft] = useState(false);
  const discardInFlightRef = useRef(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState<CreatedResult | null>(null);
  const [supplierLinkFailed, setSupplierLinkFailed] = useState(false);
  const [supplierLinkName, setSupplierLinkName] = useState("");
  const [retryLink, setRetryLink] = useState<{ productId: string; supplierId: string; supplierSku: string | null; supplierPrice: number | null; minOrderQty: number; orderMultiple: number | null; leadTimeDays: number | null; isPreferred: boolean } | null>(null);
  const [retryingLink, setRetryingLink] = useState(false);
  const [mediaFailures, setMediaFailures] = useState<WizardMediaFailure[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [enriching, setEnriching] = useState(false);
  const [enrichDraft, setEnrichDraft] = useState<EnrichmentDraft | null>(null);
  const [enrichError, setEnrichError] = useState("");

  const [catQuickAdd, setCatQuickAdd] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [catQuickError, setCatQuickError] = useState("");
  const [catQuickSaving, setCatQuickSaving] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(searchParams.get("draft"));
  const [draftSaveStatus, setDraftSaveStatus] = useState<WizardDraftSaveStatus>("idle");
  const [draftSaveError, setDraftSaveError] = useState("");
  const [mediaNotice, setMediaNotice] = useState("");
  const [draftHydrationReady, setDraftHydrationReady] = useState(!searchParams.get("draft"));
  const [variantEditorKey, setVariantEditorKey] = useState(searchParams.get("draft") ?? "new");
  const [simpleQty, setSimpleQty] = useState("");
  const [matrixLocationId, setMatrixLocationId] = useState("");
  const [stockByLocation, setStockByLocation] = useState(false);
  const [sellingIntents, setSellingIntents] = useState<WizardChannelIntent[]>([]);
  const [postCreateHold, setPostCreateHold] = useState<{ productId: string; name: string; step: "canonical" | "media" | "post_create"; message: string } | null>(null);
  const [retryingChannels, setRetryingChannels] = useState(false);
  /** Set when a restored attempt cannot be retried from here (no draft data left). */
  const [recoveryNotice, setRecoveryNotice] = useState("");
  /**
   * A REFUSED recovery write (cross-key conflict, or unreadable evidence) must
   * stay visible even after the create itself succeeded. It is tracked
   * separately from `recoveryNotice` because the success path clears that one,
   * and a rejected resolved write must never be swallowed by success handling.
   */
  const [recoveryWriteNotice, setRecoveryWriteNotice] = useState("");
  /**
   * The draft has a create whose outcome was never confirmed. Leaving the
   * screen is allowed and changes nothing; discarding is not offered, because
   * it would delete the only client-side record of that create.
   */
  const [unresolvedCreateKey, setUnresolvedCreateKey] = useState<string | null>(null);
  /**
   * The draft's recovery slot holds bytes that cannot be interpreted. The
   * earlier create outcome therefore cannot be established: creation is blocked
   * for this draft, nothing is cleared, and no new identity may be derived.
   */
  const [recoveryUnreadable, setRecoveryUnreadable] = useState(false);
  const draftTimer = useRef<number | null>(null);
  const persistSeq = useRef(0);
  /** Latest committed selling intents. Async image removal must prune this, not a stale closure. */
  const sellingIntentsRef = useRef<WizardChannelIntent[]>([]);
  /** Latest committed gallery. Async removal reconciles against this at commit time. */
  const draftMediaRef = useRef<DraftMediaView[]>([]);
  /**
   * Media mutations in flight. An upload's asset id only exists after the file
   * is stored, so absence from a gallery snapshot is never evidence of deletion
   * while one of these is running.
   */
  const pendingUploadsRef = useRef(0);
  /** Assets uploaded and attached, but not yet hydrated into the committed gallery. */
  const inFlightAssetIdsRef = useRef<Set<string>>(new Set());
  /** True once the draft media rows have been read successfully (gallery is authoritative). */
  const mediaLibraryReadyRef = useRef(false);
  /** Latest committed media-transfer failures, read by settleCreatedAttempt. */
  const mediaFailuresRef = useRef<WizardMediaFailure[]>([]);
  /** Idempotency for the supplier-link step: never re-link a link that already succeeded. */
  const supplierLinkedRef = useRef(false);
  /** Completion state carried across post-create retries. */
  const channelOutcomesRef = useRef<ChannelOutcome[]>([]);
  /** One mutating retry at a time for this creation attempt. */
  const retryGuardRef = useRef<RetryGate>({ current: false });
  /** Bumped whenever the wizard abandons an attempt, so a late callback cannot write into the next one. */
  const attemptEpochRef = useRef<AttemptEpoch>({ value: 1 });
  /**
   * Advisory marker for this page instance. Used only to describe who last
   * wrote a recovery record — never as a cross-tab lock.
   */
  const attemptIdRef = useRef<string>(
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `attempt-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  );
  /** Canonical creation identity of the current attempt, for late settles. */
  const attemptCreateKeyRef = useRef("");
  const draftOwner = useRef<DraftOwner>({
    generation: 0,
    orgId: org?.id ?? null,
    draftId: searchParams.get("draft"),
  });
  const submissionRef = useRef<SubmissionLock>({ inFlight: false, idemKey: "", productId: null });
  const attemptSnapshotRef = useRef<CreateAttemptSnapshot | null>(null);
  const latestDraftRef = useRef({
    orgId: org?.id as string | undefined,
    draftId: searchParams.get("draft") as string | null,
    form: initialForm as FormState,
    variation: { options: [], variants: [], combinationCount: 0, duplicateSkus: [], hasData: false } as VariationPayload,
    openingStock: [] as { location_id: string; qty: string; unit_cost: string; reference: string; variant_sku: string }[],
    stockChoice: "none" as "none" | "record",
    simpleQty: "",
    matrixLocationId: "",
    stockByLocation: false,
    step: 0,
    created: false,
    hydrationReady: !searchParams.get("draft"),
    stepKey: "product",
    mediaCount: 0,
    sellingIntents: [] as WizardChannelIntent[],
  });
  const writeDraftSnapRef = useRef<(job: { snap: WizardDraftPersistSnap; captured: ReturnType<typeof captureDraftSave> }, knownId: string | null) => Promise<DraftPersistWriteResult>>(async () => ({ id: null, adopt: false }));
  const draftPersistQueue = useRef(createOrderedDraftPersist((job: { snap: WizardDraftPersistSnap; captured: ReturnType<typeof captureDraftSave> }, knownId: string | null) => writeDraftSnapRef.current(job, knownId)));
  const coordinatorReady = useRef(false);
  const observedUrlDraft = useRef<string | null>(searchParams.get("draft"));
  const wizardMounted = useRef(true);

  const { data } = useQuery({
    queryKey: ["create-product-refs", org?.id],
    enabled: !!org?.id,
    staleTime: 0,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const [cats, sups, locs, cons] = await Promise.all([
        supabase.from("categories").select("id, name, parent_id, status").eq("org_id", org!.id).order("name"),
        supabase.from("suppliers").select("id, name").eq("org_id", org!.id).order("name"),
        supabase.from("locations").select("id, name").eq("org_id", org!.id).order("name"),
        supabase.from("connectors").select("id, name, provider, auth_status, health_status, config").eq("org_id", org!.id),
      ]);
      return {
        categories: (cats.data ?? []) as CategoryRow[],
        suppliers: (sups.data ?? []) as SupplierRow[],
        locations: (locs.data ?? []) as LocationRow[],
        connectors: (cons.data ?? []) as { id: string; name: string | null; provider: string; auth_status: string | null; health_status: string | null; config: unknown }[],
      };
    },
  });

  const flatCategories = useMemo(
    () => flattenWithDepth((data?.categories ?? []).filter((c) => (c.status ?? "active") !== "archived")),
    [data?.categories],
  );

  /** Shared image library offered to every channel in the Selling step. */
  const wizardMediaOptions = useMemo<WizardMediaOption[]>(
    () => draftMedia.map((item) => ({
      mediaAssetId: item.mediaAssetId,
      filename: item.filename,
      isPrimary: item.isPrimary,
      src: item.src,
    })),
    [draftMedia],
  );

  const defaultLocationId = resolveMatrixLocationId(data?.locations ?? [], matrixLocationId);
  const matrixLocationName = (data?.locations ?? []).find((location) => location.id === defaultLocationId)?.name ?? null;

  useEffect(() => {
    sellingIntentsRef.current = sellingIntents;
    draftMediaRef.current = draftMedia;
  }, [sellingIntents, draftMedia]);

  /**
   * B-001: connector rows are only authoritative after the reference query
   * settles. Hydrating a draft first must not be read as "no connectors
   * available" — that erased every restored selling intent and the next
   * autosave persisted the empty array. When the query genuinely succeeds with
   * no eligible connectors, merging still clears them (a disconnected account
   * must disappear).
   */
  useEffect(() => {
    if (!shouldMergeWizardSellingIntents({ referenceLoaded: data !== undefined, hydrationReady: draftHydrationReady })) return;
    const connectors = data?.connectors ?? [];
    setSellingIntents((prev) => mergeWizardSellingIntents(prev, connectors));
  }, [data, draftHydrationReady]);

  const applyOwnedDraft = (
    state: ReturnType<typeof blankCreateDraftState<FormState, WizardChannelIntent, DraftMediaView>>,
    editorKey: string,
  ) => {
    setForm(state.form);
    setVariation(state.variation as VariationPayload);
    setOpeningStock(state.openingStock);
    setStockChoice(state.stockChoice);
    setSimpleQty(state.simpleQty);
    setMatrixLocationId(state.matrixLocationId);
    setStockByLocation(state.stockByLocation);
    setSellingIntents(state.sellingIntents);
    setDraftMedia(state.media);
    setEnrichDraft(null);
    setEnrichError(state.enrichError);
    setVariantEditorKey(editorKey);
  };

  useEffect(() => {
    const urlDraft = searchParams.get("draft");
    const previousDraft = draftOwner.current.draftId;
    if (submissionRef.current.productId && !submissionRef.current.inFlight && urlDraft !== previousDraft) {
      submissionRef.current = abandonCreateAttempt();
      attemptSnapshotRef.current = null;
      setPostCreateHold(null);
    }
    syncDraftOwner(org?.id ?? null, urlDraft);
    if (submissionRef.current.inFlight || submissionRef.current.productId) return;
    if (!org?.id || created) {
      setDraftHydrationReady(true);
      return;
    }
    if (!urlDraft) {
      if (draftId) {
        setDraftId(null);
        applyOwnedDraft(blankCreateDraftState(initialForm), "new");
        setStep(0);
      }
      // No draft means no recovery slot: an unresolved record belongs to the
      // draft it was written for and must not leak into a new product.
      setRecoveryUnreadable(false);
      setUnresolvedCreateKey(null);
      setDraftHydrationReady(true);
      return;
    }
    if (urlDraft === draftId && draftHydrationReady) return;
    let cancelled = false;
    setDraftHydrationReady(false);
    setUnresolvedCreateKey(null);
    setRecoveryUnreadable(false);
    applyOwnedDraft(blankCreateDraftState(initialForm), `pending:${urlDraft}`);
    void loadProductWizardDraft(org.id, urlDraft)
      .then((row) => {
        if (cancelled) return;
        if (!row) {
          setDraftHydrationReady(true);
          return;
        }
        const payload = (row.payload ?? {}) as { step?: number | string };
        const owned = hydrateCreateDraftState<FormState, WizardChannelIntent, DraftMediaView>(initialForm, row.payload ?? {}, []);
        applyOwnedDraft(owned, row.id);
        setStep(resolveWizardStepIndex({ rowStep: row.step, payloadStep: payload.step }));
        setDraftId(row.id);
        setDraftHydrationReady(true);
        setDraftSaveStatus("saved");
        /**
         * B-008: this draft belongs to a product that already exists. Bind the
         * attempt to that product so Create can never run a second time, and
         * restore the unfinished steps instead of a blank wizard.
         */
        const recoveryState = readCreateRecoveryState(browserRecoveryStorage(), org.id, row.id);
        const recovery = recoveryState.state === "valid" ? recoveryState.record : null;
        setRecoveryUnreadable(recoveryState.state === "unreadable");
        if (recoveryState.state === "unreadable") {
          /**
           * P2: unreadable is NOT absent. The record exists and cannot be
           * interpreted, so its creation identity is unknown. Creation is
           * blocked for this draft, no key is bound and nothing is cleared —
           * the bytes are the only evidence of a create whose outcome is
           * unknown. Navigation, logout, refresh and remount all land here
           * again, so the block survives them.
           */
          submissionRef.current = { inFlight: false, idemKey: "", productId: null };
          attemptCreateKeyRef.current = "";
          setPostCreateHold(null);
          setUnresolvedCreateKey(null);
          setRecoveryNotice(recoveryNoticeMessage("recovery_unreadable"));
        } else if (recovery && recoveryRequiresKeyReuse(recovery)) {
          /**
           * F-01/B: the create RPC was started but its outcome was never
           * recorded. This is NOT a failed create — the product may exist.
           *
           * D-03: the record's key is adopted unconditionally. Reuse is the
           * safe action, and a stale advisory attempt id must never block it.
           * The canonical key is only derived when no unresolved record exists.
           */
          submissionRef.current = { inFlight: false, idemKey: recovery.idemKey, productId: null };
          attemptCreateKeyRef.current = recovery.idemKey;
          setPostCreateHold(null);
          setUnresolvedCreateKey(recovery.idemKey);
          setRecoveryNotice(recoveryNoticeMessage("attempt_resumed"));
        } else if (recovery) {
          submissionRef.current = { inFlight: false, idemKey: recovery.idemKey, productId: recovery.productId };
          attemptCreateKeyRef.current = recovery.idemKey;
          setUnresolvedCreateKey(null);
          setPostCreateHold({
            productId: recovery.productId,
            name: recovery.productName || owned.form.name,
            step: recoveryHoldStep(recovery.stage),
            message: recoveryHoldMessage(recovery.stage),
          });
          const rebuild = canRebuildAttemptFromDraft({
            stage: recovery.stage,
            hasForm: !!owned.form.name.trim(),
          });
          setRecoveryNotice(rebuild.reason ?? "");
        } else {
          setRecoveryNotice("");
          setUnresolvedCreateKey(null);
        }
        void hydrateDraftMedia(org.id, row.id).catch(() => {
          if (cancelled) return;
          setMediaNotice("The draft image library could not be loaded. Your channel image selections are kept — reload the draft to verify them.");
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setDraftSaveStatus("error");
        setDraftSaveError((e as Error).message || "Could not load draft");
        setDraftHydrationReady(true);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org?.id, searchParams, created]);

  const syncDraftOwner = (orgId: string | null, urlDraft: string | null, forceNew = false) => {
    const resolved = resolveWizardDraftOwnership(
      draftOwner.current,
      {
        orgId,
        urlDraftId: forceNew ? null : urlDraft,
        previousUrlDraftId: forceNew ? null : observedUrlDraft.current,
        forceNew,
      },
      () => crypto.randomUUID(),
    );
    observedUrlDraft.current = forceNew ? null : urlDraft;
    if (!resolved.changed && coordinatorReady.current) return;
    if (resolved.changed) persistSeq.current += 1;
    draftOwner.current = resolved.owner;
    draftPersistQueue.current.bindOwnership(
      { generation: resolved.owner.generation, orgId: resolved.owner.orgId },
      resolved.owner.draftId,
    );
    latestDraftRef.current.orgId = resolved.owner.orgId ?? undefined;
    latestDraftRef.current.draftId = resolved.owner.draftId;
    coordinatorReady.current = true;
    if (!resolved.changed) return;
    setDraftSaveStatus("idle");
    setDraftSaveError("");
  };

  writeDraftSnapRef.current = async (job, knownId) => {
    const { snap, captured } = job;
    if (!snap.orgId) return { id: null, adopt: false };
    const stillOwns = () => draftSaveStillCurrent(captured, draftOwner.current, persistSeq.current);
    if (!stillOwns()) return { id: null, adopt: false };
    const draftId = snap.draftId ?? knownId;
    if (!draftId) return { id: null, adopt: false };
    if (wizardMounted.current) {
      setDraftSaveStatus("saving");
      setDraftSaveError("");
    }
    try {
      const id = await upsertProductWizardDraft({
        orgId: snap.orgId,
        draftId,
        title: wizardDraftTitle({ name: snap.form.name, sku: snap.form.sku, reference: snap.form.reference }),
        step: snap.stepKey,
        payload: {
          form: snap.form,
          variation: snap.variation,
          openingStock: snap.openingStock,
          stockChoice: snap.stockChoice,
          simpleQty: snap.simpleQty,
          matrixLocationId: snap.matrixLocationId,
          stockByLocation: snap.stockByLocation,
          step: snap.step,
          sellingIntents: snap.sellingIntents,
        },
      });
      const savedId = typeof id === "string" ? id : null;
      if (!wizardMounted.current || !stillOwns()) return { id: null, adopt: false };
      if (savedId !== draftId) {
        setDraftSaveStatus("error");
        setDraftSaveError("Could not save draft");
        return { id: null, adopt: false };
      }
      draftOwner.current = { ...draftOwner.current, draftId: savedId };
      setDraftId(savedId);
      latestDraftRef.current.draftId = savedId;
      if (searchParams.get("draft") !== savedId) setSearchParams({ draft: savedId }, { replace: true });
      setDraftSaveStatus("saved");
      return { id: savedId, adopt: true };
    } catch (e) {
      if (!wizardMounted.current || !stillOwns()) return { id: null, adopt: false };
      setDraftSaveStatus("error");
      setDraftSaveError((e as Error).message || "Could not save draft");
      return { id: null, adopt: false };
    }
  };

  const persistDraft = (): Promise<string | null> => {
    if (discardInFlightRef.current) return Promise.resolve(null);
    const current = latestDraftRef.current;
    if (!current.orgId) return Promise.resolve(null);
    const snap = cloneWizardDraftSnap(current);
    const owner = draftOwner.current;
    if (owner.orgId && snap.orgId === owner.orgId) snap.draftId = owner.draftId;
    if (!shouldAutosaveWizardDraft({
      created: snap.created,
      hydrationReady: snap.hydrationReady,
      meaningful: wizardDraftIsMeaningful({
        name: snap.form.name,
        sku: snap.form.sku,
        reference: snap.form.reference,
        description: snap.form.description,
        sellingPrice: snap.form.sellingPrice,
        mediaCount: snap.mediaCount,
      }),
    })) return Promise.resolve(snap.draftId);
    const seq = ++persistSeq.current;
    const captured = captureDraftSave(owner, seq);
    return draftPersistQueue.current({ snap, captured }, { generation: captured.generation, orgId: captured.orgId });
  };

  /**
   * Load the draft media rows and (by default) reconcile channel selections
   * against them. Returns the rows that were applied, or null when the wizard
   * has meanwhile moved to another draft (a late callback must not write).
   *
   * `skipPrune` is used by image removal, which reconciles against a
   * deliberately wider evidence set instead of this single authoritative read.
   * In that mode a row that exists in the live gallery but is missing from the
   * reload (a concurrent upload whose attach committed after the query) is kept
   * rather than dropped, and the detached row is dropped explicitly.
   */
  const hydrateDraftMedia = async (
    orgId: string,
    id: string,
    opts?: { skipPrune?: boolean; droppingRowId?: string },
  ): Promise<DraftMediaView[] | null> => {
    const captured = captureDraftMedia(draftOwner.current, id);
    const rows = await loadWizardDraftMedia(orgId, id);
    const keys = rows.map((row) => row.media_assets?.storage_key).filter((key): key is string => !!key);
    const signed = await signMediaUrls(keys);
    if (!draftMediaStillCurrent(captured, draftOwner.current)) return null;
    // A successful read is what makes the gallery authoritative. Anything that
    // depends on "the asset is missing therefore it was deleted" checks this.
    mediaLibraryReadyRef.current = true;
    const loaded: DraftMediaView[] = rows.map((row) => ({
      id: row.id,
      mediaAssetId: row.media_asset_id,
      storageKey: row.media_assets?.storage_key ?? "",
      filename: row.media_assets?.original_filename ?? "image",
      position: row.position,
      isPrimary: row.is_primary,
      src: row.media_assets?.storage_key ? (signed[row.media_assets.storage_key] ?? null) : null,
    }));
    const view = opts?.skipPrune
      ? [
        ...loaded,
        ...draftMediaRef.current.filter((row) => row.id !== opts?.droppingRowId
          && !loaded.some((freshRow) => freshRow.mediaAssetId === row.mediaAssetId)),
      ]
      : loaded;
    setDraftMedia(view);
    draftMediaRef.current = view;
    latestDraftRef.current.mediaCount = view.length;
    if (!opts?.skipPrune) {
      // A resumed draft may point at an image that no longer exists in the library.
      // Those accounts fall back to inherit instead of an unsafe override.
      const assetIds = rows.map((row) => row.media_asset_id);
      setSellingIntents((prev) => pruneWizardChannelPrimaryMedia(prev, assetIds).intents);
    }
    return view;
  };

  useEffect(() => {
    latestDraftRef.current = {
      orgId: org?.id,
      draftId,
      form,
      variation,
      openingStock,
      stockChoice,
      simpleQty,
      matrixLocationId,
      stockByLocation,
      step,
      created: !!created,
      hydrationReady: draftHydrationReady,
      stepKey: WIZARD_STEP_KEYS[step] ?? "product",
      mediaCount: draftMedia.length,
      sellingIntents,
    };
    if (discardInFlightRef.current || !shouldAutosaveWizardDraft({
      created: !!created,
      hydrationReady: draftHydrationReady,
      meaningful: wizardDraftIsMeaningful({ name: form.name, sku: form.sku, reference: form.reference, description: form.description, sellingPrice: form.sellingPrice, mediaCount: draftMedia.length }),
    })) {
      return;
    }
    if (draftTimer.current) window.clearTimeout(draftTimer.current);
    draftTimer.current = window.setTimeout(() => { void persistDraft(); }, 900);
    return () => { if (draftTimer.current) window.clearTimeout(draftTimer.current); };
  }, [org?.id, form, variation, openingStock, stockChoice, simpleQty, matrixLocationId, stockByLocation, step, created, draftHydrationReady, draftId, draftMedia.length, sellingIntents, discardingDraft]);

  useEffect(() => {
    wizardMounted.current = true;
    const flush = () => { if (draftTimer.current) window.clearTimeout(draftTimer.current); void persistDraft(); };
    const onHide = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("beforeunload", flush);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      wizardMounted.current = false;
      window.removeEventListener("beforeunload", flush);
      document.removeEventListener("visibilitychange", onHide);
      flush();
    };
  }, [org?.id]);

  const discardDraft = async () => {
    if (discardInFlightRef.current) return;
    if (!draftHydrationReady || !org?.id || draftOwner.current.orgId !== org.id || !draftOwner.current.draftId) {
      setError("The draft is still loading. Wait for it to finish before discarding.");
      return;
    }
    if (saving || mediaBusy || pendingUploadsRef.current > 0) {
      setError("Wait for the current product or image operation to finish before discarding.");
      return;
    }
    // D-02: the button state and the guard must agree, and a refusal must never
    // be silent. Leaving a draft and abandoning its creation are different:
    // discarding an unresolved creation would delete the only client-side record
    // of a product that may already exist.
    if (submissionRef.current.inFlight) {
      setError("This draft is still creating a product. Wait for it to finish before discarding.");
      return;
    }
    const recoveryState = readCreateRecoveryState(browserRecoveryStorage(), org?.id, draftId);
    const existingRecord = recoveryState.state === "valid" ? recoveryState.record : null;
    const exit = draftExitState({
      lock: submissionRef.current,
      record: existingRecord,
      recoveryUnreadable: recoveryState.state === "unreadable",
    });
    if (exit === "product_exists") {
      const productId = submissionRef.current.productId || existingRecord?.productId;
      setError("This draft already created a product, so it cannot be discarded here. Open that product instead.");
      if (productId) navigate(`/products/${productId}`);
      return;
    }
    if (exit === "recovery_unreadable") {
      // P2: an unreadable record is not an empty one. Discarding would delete
      // the only evidence of a create whose outcome cannot be established.
      setRecoveryUnreadable(true);
      setRecoveryNotice(recoveryNoticeMessage("recovery_unreadable"));
      setError("This draft's earlier create cannot be established, so the draft and its record are kept. Discarding is not available for this draft.");
      return;
    }
    if (exit === "creation_unresolved") {
      setRecoveryNotice(recoveryNoticeMessage("pending_unknown"));
      setError("This draft's create has not reported back. It is kept, with its creation identity, so the attempt can still be resumed. Check the Products page before starting again.");
      return;
    }
    if (!window.confirm("Discard this draft? Entered product information will be removed.")) return;
    const captured = { ...draftOwner.current };
    const stillOwns = () => wizardMounted.current && draftMediaStillCurrent(captured, draftOwner.current);
    discardInFlightRef.current = true;
    setDiscardingDraft(true);
    setError("");
    if (draftTimer.current) window.clearTimeout(draftTimer.current);
    try {
      // Settle this editor's queued saves before sending the delete. This
      // coordinates client promises, not other tabs or server transactions.
      // New saves/creates are refused until this operation settles.
      await draftPersistQueue.current.whenIdle();
      if (!stillOwns()) return;
      const latestRecovery = readCreateRecoveryState(browserRecoveryStorage(), captured.orgId, captured.draftId);
      if (draftExitState({
        lock: submissionRef.current,
        record: latestRecovery.state === "valid" ? latestRecovery.record : null,
        recoveryUnreadable: latestRecovery.state === "unreadable",
      }) !== "clear") {
        setError("This draft now has an earlier create to resolve. Its draft and recovery record were kept.");
        return;
      }
      if (captured.orgId && captured.draftId) {
        const cleared = clearCreateRecoveryOwned(browserRecoveryStorage(), {
          orgId: captured.orgId,
          draftId: captured.draftId,
          attemptKey: attemptCreateKeyRef.current,
          attemptId: attemptIdRef.current,
        });
        if (cleared === "refused") {
          setRecoveryNotice(
            recoveryState.state === "unreadable"
              ? recoveryNoticeMessage("recovery_unreadable")
              : recoveryNoticeMessage("identity_retained"),
          );
          setError("This draft's recovery record was left untouched, because it holds a creation identity this window cannot replace. Nothing was deleted.");
          return;
        }
        const result = await discardProductWizardDraft(captured.orgId, captured.draftId);
        const keys = result?.removed_storage_keys ?? [];
        if (keys.length) {
          try { await removePrivateMediaObjects(keys); } catch { /* archived rows remain; storage cleanup is best-effort */ }
        }
      }
      if (!stillOwns()) return;
      // Mint the next identity only after the server confirms this discard, and
      // only if the operator is still viewing the draft that was discarded.
      syncDraftOwner(captured.orgId, null, true);
      setDraftId(null);
      setSearchParams({}, { replace: true });
      setForm(initialForm);
      setVariation({ options: [], variants: [], combinationCount: 0, duplicateSkus: [], hasData: false });
      setOpeningStock([]);
      setSimpleQty("");
      setMatrixLocationId("");
      setStep(0);
      setDraftSaveStatus("idle");
      setDraftSaveError("");
      setVariantEditorKey("new");
      setDraftHydrationReady(true);
      setDraftMedia([]);
      setSellingIntents([]);
      setStockChoice("none");
      setStockByLocation(false);
      setEnrichDraft(null);
      setEnrichError("");
      setMediaNotice("");
      setRecoveryNotice("");
      setRecoveryWriteNotice("");
      setUnresolvedCreateKey(null);
      setRecoveryUnreadable(false);
      attemptCreateKeyRef.current = "";
    } catch {
      if (stillOwns()) {
        setError("Could not confirm that this draft was discarded. Your entered details are still here. Try again or leave the draft.");
      }
    } finally {
      discardInFlightRef.current = false;
      if (wizardMounted.current) setDiscardingDraft(false);
    }
  };

  const steps = useMemo(() => {
    const variantsLabel = form.productType === "variation" ? "Variants & Stock" : form.productType === "bundle" ? "Bundle" : "Stock";
    const variantsIcon = form.productType === "variation" ? "ri-grid-line" : form.productType === "bundle" ? "ri-box-3-line" : "ri-inbox-line";
    return [
      { key: "product", label: "Product", icon: "ri-file-list-line" },
      { key: "variants", label: variantsLabel, icon: variantsIcon },
      { key: "selling", label: "Selling", icon: "ri-store-2-line" },
      { key: "review", label: "Review", icon: "ri-check-double-line" },
    ];
  }, [form.productType]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const wizardIdentifierError = (): string | null => {
    if (form.identifiersNotApplicable) {
      if (form.gtin.trim() || form.upc.trim() || form.ean.trim()) {
        return "Turn off “product has no barcode” before entering a real GTIN/UPC/EAN.";
      }
      return null;
    }
    for (const [type, value] of [
      ["gtin", form.gtin],
      ["upc", form.upc],
      ["ean", form.ean],
    ] as const) {
      if (!value.trim()) continue;
      const err = validateIdentifierValue(type, value);
      if (err) return `${type.toUpperCase()} ${err}. Enter a real barcode, or leave the field blank.`;
    }
    return null;
  };

  const canProceed = (): boolean => {
    const productOk = form.name.trim().length > 0
      && !(isCanonicalCreateRequired("sku", form.productType) && (form.sku.trim().length === 0 || skuValidationError(form.sku) !== null))
      && !wizardIdentifierError();
    const variantsOk = form.productType === "variation"
      ? variation.hasData
        && variation.duplicateSkus.length === 0
        && !matrixLocationProblem({
          productType: form.productType,
          defaultLocationId,
          variants: variation.variants,
        })
      : true;
    return wizardCanLeaveStep(WIZARD_STEP_KEYS[step] ?? "review", { product: productOk, variants: variantsOk });
  };

  const next = () => {
    if (submissionRef.current.inFlight || !canProceed()) return;
    const nextStep = Math.min(step + 1, steps.length - 1);
    const leavingSelling = (WIZARD_STEP_KEYS[step] ?? "product") === "selling";
    const leave = leavingSelling ? wizardSellingLeavePayload(sellingIntents) : null;
    latestDraftRef.current = {
      ...latestDraftRef.current,
      sellingIntents: leave?.sellingIntents ?? captureWizardSellingIntents(sellingIntents),
      step: nextStep,
      stepKey: leave?.stepKey ?? (WIZARD_STEP_KEYS[nextStep] ?? "review"),
    };
    if (draftTimer.current) window.clearTimeout(draftTimer.current);
    void persistDraft();
    setStep(nextStep);
  };
  const back = () => {
    if (submissionRef.current.inFlight) return;
    if (draftTimer.current) window.clearTimeout(draftTimer.current);
    void persistDraft();
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleFiles = (files: FileList | null) => {
    if (discardInFlightRef.current || !files || !org?.id) return;
    const incoming = Array.from(files);
    const captured = captureDraftMedia(draftOwner.current, draftId ?? "");
    // F-03: register the upload BEFORE it starts. Until its asset id exists it
    // is invisible to every gallery snapshot, so a concurrent removal must know
    // an upload is unresolved rather than reading the absence as a deletion.
    pendingUploadsRef.current += 1;
    void (async () => {
      setMediaBusy(true);
      try {
        latestDraftRef.current.mediaCount = Math.max(latestDraftRef.current.mediaCount, draftMedia.length + incoming.length);
        const id = await persistDraft();
        if (draftOwner.current.generation !== captured.generation || draftOwner.current.orgId !== captured.orgId) return;
        if (!id) {
          setError("Save the draft before adding images.");
          return;
        }
        for (const file of incoming) {
          const invalid = validateFile(file);
          if (invalid) throw new Error(invalid);
          const asset = await uploadDraftMediaAsset(org.id, id, file);
          // The asset now has an id but is not in the committed gallery yet.
          inFlightAssetIdsRef.current.add(asset.id);
          await attachWizardDraftMedia({ orgId: org.id, draftId: id, mediaAssetId: asset.id });
        }
        if (draftOwner.current.generation !== captured.generation || draftOwner.current.orgId !== captured.orgId) return;
        await hydrateDraftMedia(org.id, id);
        if (draftOwner.current.generation !== captured.generation) return;
        setDraftSaveStatus("saved");
      } catch (e) {
        if (draftOwner.current.generation !== captured.generation) return;
        setDraftSaveStatus("error");
        setDraftSaveError((e as Error).message || "Could not save image");
        setError((e as Error).message || "Could not save image");
      } finally {
        pendingUploadsRef.current = Math.max(0, pendingUploadsRef.current - 1);
        if (pendingUploadsRef.current === 0) inFlightAssetIdsRef.current.clear();
        setMediaBusy(false);
      }
    })();
  };

  /**
   * Remove one draft image (B-006).
   *
   * The detach is asynchronous, and the operator can upload and assign another
   * image while it runs. Pruning against the gallery snapshot captured at the
   * start of the removal therefore reverted that newer selection to inherit.
   *
   * Now the removal re-reads the authoritative draft media rows after the
   * delete and reconciles against:
   *   - the reloaded rows (authoritative when the reload succeeds),
   *   - the latest committed gallery (so a concurrent upload always survives),
   *   - and only ever treats the positively detached asset as gone when the
   *     reload failed.
   */
  const removeDraftImage = (draftMediaId: string) => {
    if (discardInFlightRef.current || !org?.id || !draftId) return;
    const removalOrgId = org.id;
    const removalDraftId = draftId;
    const captured = captureDraftMedia(draftOwner.current, removalDraftId);
    const galleryAtStart = draftMedia.map((item) => item.mediaAssetId);
    const removedAssetId = draftMedia.find((item) => item.id === draftMediaId)?.mediaAssetId ?? null;
    /** Gallery before the post-delete reload: contains any concurrent upload already attached. */
    const liveBeforeReload = draftMediaRef.current.map((row) => row.mediaAssetId);
    void (async () => {
      try {
        const result = await detachWizardDraftMedia(removalOrgId, removalDraftId, draftMediaId);
        if (result.storage_key) {
          try { await removePrivateMediaObjects([result.storage_key]); } catch { /* archived */ }
        }
        if (!draftMediaStillCurrent(captured, draftOwner.current)) return;

        let reloadedAssetIds: string[] | null = null;
        try {
          const refreshed = await hydrateDraftMedia(removalOrgId, removalDraftId, {
            skipPrune: true,
            droppingRowId: draftMediaId,
          });
          if (refreshed) reloadedAssetIds = refreshed.map((row) => row.mediaAssetId);
        } catch {
          // The detach is committed but the gallery could not be re-read. That is
          // not evidence that any other asset was deleted.
          reloadedAssetIds = null;
        }
        if (!draftMediaStillCurrent(captured, draftOwner.current)) return;

        if (reloadedAssetIds === null) {
          // Drop only the row we know was detached, locally, and keep the rest.
          const remainingRows = draftMediaRef.current.filter((row) => row.id !== draftMediaId);
          draftMediaRef.current = remainingRows;
          latestDraftRef.current.mediaCount = remainingRows.length;
          setDraftMedia(remainingRows);
        }

        const outcome = reconcileDraftMediaRemoval({
          removedAssetId,
          galleryAtStart,
          reloadedAssetIds,
          // Union of the gallery before the reload and after it, so an upload
          // that attached in between is never mistaken for a deletion.
          liveAssetIds: [...liveBeforeReload, ...draftMediaRef.current.map((row) => row.mediaAssetId)],
          // F-03: an upload that is still running (or has uploaded but not yet
          // hydrated) is a pending addition, never a deletion.
          pendingAssetIds: [...inFlightAssetIdsRef.current],
          pendingUploadCount: pendingUploadsRef.current,
        });
        if (outcome.deferred) {
          // An upload is unresolved, so this gallery cannot prove anything was
          // deleted. Pruning is deferred to that upload's own authoritative
          // hydrate instead of guessing here.
          return;
        }
        // Prune the LATEST committed intents: a selection made while the detach
        // was in flight must survive unless its own asset is genuinely gone.
        const pruned = pruneWizardChannelPrimaryMedia(sellingIntentsRef.current, outcome.keepAssetIds);
        if (pruned.cleared.length > 0) {
          sellingIntentsRef.current = pruned.intents;
          setSellingIntents(pruned.intents);
          setMediaNotice(`${pruned.cleared.map((row) => row.displayName).join(", ")} now uses the Product Master primary image because the selected image was removed.`);
        }
        const warning = draftMediaRemovalWarning(outcome);
        if (warning) setMediaNotice(warning);
      } catch (e) {
        if (!draftMediaStillCurrent(captured, draftOwner.current)) return;
        // The image is still attached; removal must not read as done.
        setError(draftMediaRemovalFailure(e));
      }
    })();
  };

  const setPrimaryDraftImage = (mediaAssetId: string) => {
    if (discardInFlightRef.current || !org?.id || !draftId) return;
    const captured = captureDraftMedia(draftOwner.current, draftId);
    void (async () => {
      try {
        await attachWizardDraftMedia({ orgId: org.id, draftId, mediaAssetId, isPrimary: true });
        if (!draftMediaStillCurrent(captured, draftOwner.current)) return;
        await hydrateDraftMedia(org.id, draftId);
      } catch (e) {
        if (!draftMediaStillCurrent(captured, draftOwner.current)) return;
        setError((e as Error).message || "Could not set primary image");
      }
    })();
  };

  const addOpeningStockRow = () => setOpeningStock((rows) => [...rows, { location_id: "", qty: "", unit_cost: "", reference: "", variant_sku: "" }]);
  const removeOpeningStockRow = (i: number) => setOpeningStock((rows) => rows.filter((_, idx) => idx !== i));
  const updateOpeningStockRow = (i: number, key: string, value: string) =>
    setOpeningStock((rows) => rows.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));

  const unlockFailedCreate = () => {
    const next = failSubmission(submissionRef.current);
    submissionRef.current = next;
    setSaving(false);
  };

  /** Opening stock rows for the success screen, derived from the SAME snapshot the create path used. */
  const createdStockRows = (attempt: CreateAttemptSnapshot) => projectOpeningStock({
    productType: attempt.form.productType,
    defaultLocationId: attempt.defaultLocationId,
    stockChoice: attempt.stockChoice,
    simpleQty: attempt.simpleQty,
    productCost: num(attempt.form.costPrice),
    variants: attempt.variation.variants,
    locationRows: attempt.openingStock,
  }).map((row) => ({
    locationId: row.location_id,
    locationName: attempt.locations.find((location) => location.id === row.location_id)?.name ?? "a location",
    qty: row.qty,
    variantSku: row.sku ?? null,
  }));

  /**
   * Rebuild the attempt from the current wizard state. Used by a retry that was
   * restored from a draft after a reload, where the original in-memory snapshot
   * is gone but the draft still holds every input the sequence needs.
   */
  const buildAttemptSnapshot = (): CreateAttemptSnapshot | null => {
    if (!org?.id) return null;
    return {
      orgId: org.id,
      draftId,
      form: { ...form, supplier: { ...form.supplier } },
      variation: {
        ...variation,
        options: variation.options.map((option) => ({ ...option, values: option.values.map((value) => ({ ...value })) })),
        variants: variation.variants.map((variant) => ({ ...variant })),
      },
      openingStock: openingStock.map((row) => ({ ...row })),
      stockChoice,
      simpleQty,
      defaultLocationId,
      locations: (data?.locations ?? []).map((location) => ({ ...location })),
      sellingIntents: sellingIntents.map((intent) => ({ ...intent, priceOverride: intent.priceOverride ? { ...intent.priceOverride } : null })),
      hasMedia: draftMedia.length > 0,
      mediaAssetIds: draftMedia.map((item) => item.mediaAssetId),
    };
  };

  /**
   * Persist a RESOLVED recovery record (a confirmed product plus the stage it
   * reached) through the guarded writer.
   *
   * A refused write is never silent: the operator is told the record was not
   * updated, because the wizard must not continue as if the recovery slot now
   * describes this attempt. A conflict always preserves the existing record, so
   * a late response from this tab cannot erase another tab's unresolved
   * creation identity.
   */
  const persistResolvedRecovery = (record: {
    orgId: string;
    draftId: string;
    productId: string;
    productName: string;
    idemKey: string;
    stage: "canonical" | "media" | "post_create";
  }, opts?: { silent?: boolean }) => {
    const outcome = writeResolvedCreateRecovery(browserRecoveryStorage(), {
      ...record,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      attemptId: attemptIdRef.current,
    });
    if (outcome === "conflict" || outcome === "unreadable") {
      const message = recoveryNoticeMessage(
        outcome === "unreadable" ? "recovery_unreadable" : "recovery_write_conflict",
      );
      if (!opts?.silent) {
        setRecoveryNotice(message);
        // Kept separate so a later success render cannot erase the fact that the
        // recovery slot does NOT describe this attempt.
        setRecoveryWriteNotice(message);
        if (outcome === "unreadable") setRecoveryUnreadable(true);
        // The slot does not describe this attempt, so the draft is not settled.
        setUnresolvedCreateKey(record.idemKey);
      }
    }
    return outcome;
  };

  /**
   * B-008: settle the lifecycle of a product that already exists.
   *
   * While any post-create obligation is unresolved the draft is the only place
   * the media and channel selections exist, so it is kept and a recovery record
   * is written — that record is what stops a reload from creating the product
   * again. Once everything has settled the record is cleared and the draft is
   * discarded. A failed discard is never reported as a product failure: the
   * product exists and the leftover draft is only housekeeping.
   */
  const settleCreatedAttempt = async (params: {
    attempt: CreateAttemptSnapshot;
    productId: string;
    outcomes: ChannelOutcome[];
    supplierLinked: boolean;
    /**
     * Real unresolved draft-media transfer failures. This used to be hardcoded
     * to 0, which let the draft settle (and be discarded) while the product's
     * images had not moved across.
     */
    mediaFailures: number;
    epoch: number;
  }) => {
    const { attempt, productId, outcomes, supplierLinked, mediaFailures, epoch } = params;
    if (!attemptEpochIsCurrent(attemptEpochRef.current, epoch)) return;
    const obligations = derivePostCreateObligations({ outcomes, supplierLinked, mediaFailures });
    const storage = browserRecoveryStorage();
    const keepDraft = hasUnfinishedPostCreate(obligations);

    if (keepDraft) {
      if (attempt.draftId) {
        /**
         * F-06: the stage must describe what is actually outstanding. A draft
         * kept because its images never transferred needs the `media` recovery
         * path, not a generic post_create record.
         */
        const stage = incompletePostCreateStage(obligations);
        persistResolvedRecovery({
          orgId: attempt.orgId,
          draftId: attempt.draftId,
          productId,
          productName: attempt.form.name,
          // The attempt's own creation identity, captured before the lock is
          // finished. Reading the lock here used to store an empty key.
          idemKey: attemptCreateKeyRef.current,
          stage,
        });
      }
      return;
    }

    if (attempt.draftId) {
      const cleared = clearCreateRecoveryOwned(storage, {
        orgId: attempt.orgId,
        draftId: attempt.draftId,
        attemptKey: attemptCreateKeyRef.current,
        attemptId: attemptIdRef.current,
      });
      if (cleared === "refused") {
        /**
         * The slot does NOT hold this attempt's resolved record — a different
         * creation identity (or unreadable evidence) is still there. Discarding
         * the draft would delete the only recovery payload that identity has,
         * so both are preserved and the operator is told.
         */
        setRecoveryWriteNotice((prev) => prev || recoveryNoticeMessage("identity_retained"));
        return;
      }
    }
    if (!attempt.draftId || !shouldDiscardDraftAfterCreate(obligations)) return;
    if (!attemptEpochIsCurrent(attemptEpochRef.current, epoch)) return;
    persistSeq.current += 1;
    draftOwner.current = { ...draftOwner.current, generation: draftOwner.current.generation + 1, draftId: null };
    draftPersistQueue.current.bindOwnership(
      { generation: draftOwner.current.generation, orgId: draftOwner.current.orgId },
      null,
    );
    try {
      await discardProductWizardDraft(attempt.orgId, attempt.draftId);
    } catch {
      // Leftover draft only. The product itself was created successfully.
    }
  };

  /**
   * Execute only the channel steps that are still unresolved for this attempt.
   *
   * Shared by the create path and every retry. Completion is tracked per
   * connector id + stage, so two eBay accounts never collapse into one another
   * and a retry never re-creates a listing that already exists. The media step
   * uses the existing apply plan, which enforces live-gallery eligibility and
   * per-connector isolation.
   */
  const runChannelDrafts = async (
    attempt: CreateAttemptSnapshot,
    productId: string,
    prior: ChannelOutcome[] = [],
  ): Promise<ChannelOutcome[]> => {
    const priceVariants = attempt.form.productType === "variation"
      ? attempt.variation.variants.map((variant) => ({ sku: variant.sku, sellingPrice: variant.selling_price }))
      : [];
    const selected = attempt.sellingIntents.filter((row) => row.selected);
    const mediaPlan = wizardChannelMediaApplyPlan(attempt.sellingIntents, attempt.mediaAssetIds);
    const mediaByConnector = new Map(mediaPlan.map((row) => [row.connectorId, row.mediaAssetId]));
    const steps = planChannelSteps(selected, prior, mediaByConnector, {
      productId,
      // F-04 case A: a removed override may only be resolved when the image
      // library itself is known-good. A failed gallery read is not evidence.
      resolveSupersededMediaFailure: mediaLibraryReadyRef.current,
    });
    const produced: ChannelOutcome[] = [];

    const failListing = (intent: WizardChannelIntent, message: string) => {
      produced.push({
        connectorId: intent.connectorId,
        provider: intent.provider,
        displayName: intent.displayName,
        channel: `${intent.displayName} (${intent.provider})`,
        stage: "listing",
        status: "failed",
        listingId: null,
        message,
        productId,
      });
      return { success: false as const, error: message };
    };

    await recordWizardChannelDrafts(selected, async (intent) => {
      const step = steps.find((row) => row.connectorId === intent.connectorId);
      if (!step) return { success: true };
      const base = {
        connectorId: intent.connectorId,
        provider: intent.provider,
        displayName: intent.displayName,
        channel: `${intent.displayName} (${intent.provider})`,
        // Scope every outcome to the product it was written for.
        productId,
      };

      let listingId = step.listingId;

      if (step.createListing) {
        if (intent.priceOverride) {
          const priceError = wizardPriceAmountError(intent.priceOverride.strategy, intent.priceOverride.amount);
          if (priceError) return failListing(intent, priceError);
        }
        const quote = resolveWizardChannelPrice({
          productPrice: num(attempt.form.sellingPrice),
          variants: priceVariants,
          connectorDefault: intent.connectorPricing ?? { strategy: "inherit", config: {} },
          productOverride: intent.priceOverride ?? null,
        });
        const quoteError = wizardQuotePriceError(quote);
        if (quoteError) return failListing(intent, quoteError);
        const pricing = wizardListingPricePayload(quote);
        if (!pricing) return failListing(intent, "Enter a valid price rule before saving this channel.");
        const { data: fresh, error: connectorError } = await supabase
          .from("connectors")
          .select("id, org_id, provider, auth_status, health_status")
          .eq("org_id", attempt.orgId)
          .eq("id", intent.connectorId)
          .maybeSingle();
        if (connectorError) return failListing(intent, connectorError.message);
        const decision = wizardConnectorSaveDecision(attempt.orgId, fresh);
        if (decision.save === false) return failListing(intent, decision.error);
        const category = wizardDraftListingCategory(intent);
        const listing = await createChannelListing({
          orgId: attempt.orgId,
          connectorId: intent.connectorId,
          productId,
          priceStrategy: pricing.priceStrategy,
          providerConfig: pricing.providerConfig,
          channelCategoryId: category.channelCategoryId ?? undefined,
          channelCategoryName: category.channelCategoryName ?? undefined,
          listingType: attempt.form.productType === "variation" ? "multi_variation" : "single",
          provider: intent.provider,
        });

        if (listing.success) {
          listingId = listing.listing_id ?? null;
          produced.push({ ...base, stage: "listing", status: "created", listingId, message: null });
        } else if (listing.code === "already_exists" && listing.listing_id) {
          // B-004: `already_exists` is only adopted when the returned listing is
          // proven to be this organisation's, this product's and this
          // connector's. Anything else is a real failure.
          const { data: existing, error: readError } = await supabase
            .from("channel_listings")
            .select("id, org_id, product_id, connector_id")
            .eq("org_id", attempt.orgId)
            .eq("id", listing.listing_id)
            .maybeSingle();
          if (readError) return failListing(intent, `An existing listing could not be verified: ${readError.message}`);
          const verdict = classifyExistingListing({
            orgId: attempt.orgId,
            productId,
            connectorId: intent.connectorId,
            listing: existing,
          });
          if (!verdict.adopt) return failListing(intent, verdict.reason);
          listingId = listing.listing_id;
          produced.push({ ...base, stage: "listing", status: "adopted", listingId, message: null });
        } else {
          return failListing(intent, listing.error ?? "draft listing was not saved");
        }
      }

      if (step.resolveMedia && listingId) {
        // F-04 case A: the operator intentionally removed this account's image
        // override, so the previous primary-image failure is no longer
        // applicable. Resolve it without replaying a doomed apply.
        produced.push({
          ...base,
          stage: "primary_media",
          status: "adopted",
          listingId,
          message: "This account now uses the Product Master primary image.",
        });
      }

      if (step.applyMedia && listingId && step.mediaAssetId) {
        /**
         * F-02: a listing id may only be used for a media mutation once it is
         * proven to belong to this organisation, this product and this connector
         * account. A stale id from another attempt (or a duplicated connector)
         * fails closed here instead of writing to the wrong listing.
         */
        const { data: ownedListing, error: ownershipError } = await supabase
          .from("channel_listings")
          .select("id, org_id, product_id, connector_id")
          .eq("org_id", attempt.orgId)
          .eq("id", listingId)
          .maybeSingle();
        const ownership = ownershipError
          ? { adopt: false, reason: `The listing could not be verified before applying the image: ${ownershipError.message}` }
          : classifyExistingListing({
            orgId: attempt.orgId,
            productId,
            connectorId: intent.connectorId,
            listing: ownedListing,
          });
        if (!ownership.adopt) {
          produced.push({
            ...base,
            stage: "primary_media",
            status: "failed",
            listingId: null,
            message: ownership.reason,
          });
          return { success: false, error: ownership.reason };
        }
        const applied = await applyWizardChannelPrimaryMedia({
          orgId: attempt.orgId,
          listingId,
          mediaAssetId: step.mediaAssetId,
          galleryAssetIds: attempt.mediaAssetIds,
        });
        const message = applied.success ? null : (applied.error ?? "Channel image was not applied");
        produced.push({
          ...base,
          stage: "primary_media",
          status: applied.success ? "created" : "failed",
          listingId,
          message,
        });
        // The listing exists — this is a media-only failure, and the outcome
        // above says so. It must never read as "the listing was not saved".
        if (!applied.success) return { success: false, error: message ?? "Channel image was not applied" };
      }

      return { success: true };
    });

    // Only this product's prior outcomes are folded in. A row written for a
    // different product can never be adopted by this attempt.
    return mergeChannelOutcomes(prior.filter((row) => !row.productId || row.productId === productId), produced);
  };

  /**
   * Link the selected supplier through the canonical supplier–product service.
   * Shared by create and retry, and safe to call twice: the service upserts on
   * the natural key (product, supplier, variant).
   */
  const runSupplierLink = async (
    attempt: CreateAttemptSnapshot,
    productId: string,
    epoch?: number,
  ): Promise<boolean> => {
    /**
     * F-02 (Version 372): the supplier warning and its retry payload are shared
     * attempt state. A late supplier result from an abandoned attempt must not
     * overwrite the new attempt's warning or its retry link, so every write goes
     * through this gate.
     */
    const ownsAttempt = () => epoch === undefined || attemptEpochIsCurrent(attemptEpochRef.current, epoch);
    const commitLinked = () => {
      if (!ownsAttempt()) return;
      supplierLinkedRef.current = true;
      setSupplierLinkFailed(false);
      setSupplierLinkName("");
      setRetryLink(null);
    };
    const supplierId = attempt.form.supplier.supplierId;
    const linkInput = {
      productId,
      supplierId,
      variantId: null,
      supplierSku: attempt.form.supplier.supplierSku || null,
      supplierPrice: num(attempt.form.costPrice),
      minOrderQty: attempt.form.supplier.minOrderQty ? Number(attempt.form.supplier.minOrderQty) : 1,
      orderMultiple: attempt.form.supplier.orderMultiple ? Number(attempt.form.supplier.orderMultiple) : null,
      leadTimeDays: attempt.form.supplier.leadTimeDays ? Number(attempt.form.supplier.leadTimeDays) : null,
      isPreferred: attempt.form.supplier.isPreferred,
    };
    const commitFailed = (failedLink: typeof linkInput) => {
      if (!ownsAttempt()) return;
      supplierLinkedRef.current = false;
      setSupplierLinkFailed(true);
      setSupplierLinkName(
        (data?.suppliers ?? []).find((sp) => sp.id === supplierId)?.name ?? supplierId,
      );
      setRetryLink(failedLink);
    };
    if (!supplierId) {
      commitLinked();
      return true;
    }
    try {
      await linkSupplierProduct(org!.id, linkInput);
      commitLinked();
      return true;
    } catch {
      commitFailed(linkInput);
      return false;
    }
  };

  /**
   * F-01/C: look a product up by its idempotency key using the server-side
   * contract. `ok: false` means the lookup itself failed, so the outcome is
   * genuinely unknown — that is different from "no product exists".
   */
  const lookupCreatedProduct = async (
    orgId: string,
    key: string,
  ): Promise<{ productId: string | null; ok: boolean }> => {
    if (!key) return { productId: null, ok: true };
    const { data, error } = await supabase
      .from("products")
      .select("id")
      .eq("org_id", orgId)
      .eq("idempotency_key", key)
      .maybeSingle();
    if (error) return { productId: null, ok: false };
    return { productId: (data?.id as string | undefined) ?? null, ok: true };
  };

  const handleCreate = async () => {
    if (discardInFlightRef.current || submissionRef.current.inFlight || submissionRef.current.productId) return;
    /**
     * Claim the attempt synchronously so a double-click cannot start two
     * creates while the draft identity is being resolved below.
     */
    submissionRef.current = { inFlight: true, idemKey: submissionRef.current.idemKey, productId: null };
    /**
     * F-02 (Version 372): the create path can still finish after the operator has
     * abandoned the attempt. Capture the epoch it belongs to and refuse every
     * shared-state write once the wizard has moved on.
     */
    const epoch = attemptEpochRef.current.value;
    setSaving(true);
    setError("");
    // A refusal from a previous attempt is not this attempt's outcome.
    setRecoveryWriteNotice("");
    const attempt = buildAttemptSnapshot();
    if (!attempt) {
      unlockFailedCreate();
      setError("The organisation is not loaded yet. Try again in a moment.");
      return;
    }
    attemptSnapshotRef.current = attempt;

    /**
     * Refuse incomplete or silently-lossy input BEFORE any identity is written.
     */
    const submissionProblem = submissionValidationError(attempt);
    if (submissionProblem) {
      unlockFailedCreate();
      setError(submissionProblem);
      return;
    }

    /**
     * D-03 — canonical creation identity.
     *
     * One organisation + one persistent draft identity is ONE creation
     * identity. The draft is resolved FIRST, then the key is chosen:
     *   - an unresolved `pending_create` record keeps its own key, which
     *     preserves a legacy random key written by an earlier build;
     *   - otherwise the key is DERIVED from (orgId, draftId), so two tabs that
     *     load the same draft cannot mint different keys from separate state.
     * A genuinely new product gets a new draft identity via the wizard's
     * existing "new draft" path, not by reusing this one.
     */
    let attemptDraftId = attempt.draftId ?? draftId ?? null;
    if (!attemptDraftId) attemptDraftId = await persistDraft();
    if (!attemptDraftId) {
      unlockFailedCreate();
      setError("This product could not be attached to a saved draft, so no creation identity could be established. Save the draft, then try again.");
      return;
    }
    attempt.draftId = attemptDraftId;
    /**
     * P2 — the ONE creation-eligibility gate.
     *
     * Evaluated before any identity derivation and before any RPC submission.
     * An unreadable record means the earlier outcome cannot be established, so
     * no fresh identity may be derived for this draft and nothing is submitted.
     * No acknowledgement flag is consulted: acknowledgement may change copy,
     * never eligibility, and it is never persisted as authorisation.
     */
    const eligibility = evaluateCreateEligibility({
      store: browserRecoveryStorage(),
      orgId: attempt.orgId,
      draftId: attemptDraftId,
    });
    if (eligibility.allowed === false) {
      unlockFailedCreate();
      if (eligibility.reason === "unreadable") {
        setRecoveryUnreadable(true);
        setUnresolvedCreateKey(null);
        setPostCreateHold(null);
        setRecoveryNotice(recoveryNoticeMessage("recovery_unreadable"));
        setError("A create was already recorded for this draft, and that record cannot be read. Its outcome cannot be established, so nothing was created now. The record and the draft are kept — leave this page and reopen the draft to try reading it again.");
      } else {
        setRecoveryNotice(recoveryNoticeMessage("known_product"));
        setError("This draft already created a product, so Create will not run again for it. Open that product instead.");
      }
      return;
    }
    const existingRecord = eligibility.record;
    const resolvedKey = resolveCanonicalCreateKey({
      orgId: attempt.orgId,
      draftId: attemptDraftId,
      record: existingRecord,
    });
    if (!resolvedKey.key) {
      unlockFailedCreate();
      setError("The creation identity for this draft could not be resolved, so nothing was created. Reload the draft and try again.");
      return;
    }
    const key = resolvedKey.key;
    const freshIdentity = resolvedKey.source === "canonical";
    submissionRef.current = { inFlight: true, idemKey: key, productId: null };
    attemptCreateKeyRef.current = key;

    // Proactive SKU collision check before creating. The comparison uses the
    // SAME canonical contract as submission (normalizeSku), so "BLK M" and
    // "BLK-M" are caught here instead of colliding inside the database.
    // It only runs for a FRESH identity: on a retry of an existing attempt its
    // own SKU now legitimately exists, so re-checking would block recovery.
    if (freshIdentity) {
      if (attempt.form.productType !== "variation" && attempt.form.sku.trim()) {
        const sku = normalizeSku(attempt.form.sku);
        const [variantHit, productHit] = await Promise.all([
          supabase.from("variants").select("id").eq("org_id", org!.id).eq("sku", sku).maybeSingle(),
          supabase.from("products").select("id").eq("org_id", org!.id).eq("sku", sku).maybeSingle(),
        ]);
        if (variantHit.data || productHit.data) {
          unlockFailedCreate();
          setError(`SKU ${sku} already exists on this product catalogue. Choose a different SKU.`);
          return;
        }
      }
      if (attempt.form.productType === "variation" && attempt.variation.variants.length > 0) {
        const localConflicts = findCanonicalSkuCollisions(attempt.variation.variants.map((variant) => variant.sku));
        if (localConflicts.length > 0) {
          unlockFailedCreate();
          setError(
            `These variants share one canonical SKU and cannot be created: ${localConflicts
              .map((row) => `${row.sku} conflicts with ${row.conflictsWith}`)
              .join("; ")}.`,
          );
          return;
        }
        const canonical = canonicalSkuList(attempt.variation.variants.map((variant) => variant.sku));
        // Bound the existence check; the DB unique constraint remains the backstop.
        const probe = canonical.slice(0, 200);
        if (probe.length > 0) {
          const [variantHit, productHit] = await Promise.all([
            supabase.from("variants").select("sku").eq("org_id", org!.id).in("sku", probe),
            supabase.from("products").select("sku").eq("org_id", org!.id).in("sku", probe),
          ]);
          const taken = [...(variantHit.data ?? []), ...(productHit.data ?? [])]
            .map((row) => row.sku)
            .filter((sku): sku is string => !!sku);
          if (taken.length > 0) {
            unlockFailedCreate();
            setError(`These SKUs already exist in this catalogue: ${[...new Set(taken)].join(", ")}. Choose different SKUs.`);
            return;
          }
        }
      }
    }

    const tags = attempt.form.tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    /**
     * F-01/A: write the attempt's identity durably BEFORE the create RPC.
     *
     * The record is product-less on purpose: its only job at this point is to
     * survive the window between the server commit and the response handler.
     * The guarded write refuses to overwrite a product-bearing record or an
     * unresolved record with a DIFFERENT creation identity.
     *
     * If it cannot be persisted we do not start an operation whose outcome
     * would become untraceable.
     */
    const recoveryStore = browserRecoveryStorage();
    const writeOutcome = writePendingCreateRecovery(recoveryStore, {
      orgId: attempt.orgId,
      draftId: attemptDraftId,
      productName: attempt.form.name,
      idemKey: key,
      attemptId: attemptIdRef.current,
    });
    const writeAheadPersisted = writeOutcome === "written"
      && confirmCreateRecoveryPersisted(recoveryStore, {
        orgId: attempt.orgId,
        draftId: attemptDraftId,
        idemKey: key,
      });
    if (!writeAheadPersisted) {
      unlockFailedCreate();
      if (writeOutcome === "unreadable") {
        setRecoveryUnreadable(true);
        setRecoveryNotice(recoveryNoticeMessage("recovery_unreadable"));
        setError("A create was already recorded for this draft, and that record cannot be read, so nothing was created now. The record and the draft are kept.");
        return;
      }
      if (writeOutcome === "conflict") {
        setRecoveryNotice(recoveryNoticeMessage("recovery_write_conflict"));
        setError("Another window is already creating a product for this draft. Reload the draft before creating again, so both windows cannot disagree about the product's creation identity.");
        return;
      }
      // The existing storage notice: a reload could not resume this create, so
      // the operator is told to confirm the outcome rather than to retry blindly.
      setRecoveryNotice(recoveryNoticeMessage("storage_unavailable"));
      setError("The create could not be made recoverable, so it was not started. Save the draft (or allow browser storage) and try again.");
      return;
    }
    setUnresolvedCreateKey(key);

    let rpcData: productApi.CreatedProduct;
    try {
      rpcData = await productApi.createProduct({
        orgId: attempt.orgId,
        name: attempt.form.name,
        reference: attempt.form.reference || null,
        brand: attempt.form.brand || null,
        categoryId: attempt.form.categoryId || null,
        status: attempt.form.status,
        description: attempt.form.description || null,
        shortDescription: attempt.form.shortDescription || null,
        seoTitle: attempt.form.seoTitle || null,
        metaDescription: attempt.form.metaDescription || null,
        tags: tags.length ? tags : null,
        unit: attempt.form.unit || "each",
        productType: attempt.form.productType === "bundle" ? "bundle" : "physical",
        variationMode: attempt.form.productType === "variation" ? "variation" : "simple",
        sku: attempt.form.sku || null,
        barcode: attempt.form.barcode || null,
        costPrice: num(attempt.form.costPrice),
        sellingPrice: num(attempt.form.sellingPrice),
        taxProfile: attempt.form.taxProfile || null,
        weightKg: num(attempt.form.weightKg),
        lengthCm: num(attempt.form.lengthCm),
        widthCm: num(attempt.form.widthCm),
        heightCm: num(attempt.form.heightCm),
        reorderPoint: Number(attempt.form.reorderPoint) || 0,
        reorderQty: Number(attempt.form.reorderQty) || 0,
        options: attempt.form.productType === "variation" ? attempt.variation.options : [],
        variants: attempt.form.productType === "variation" ? attempt.variation.variants : [],
        initialStock: projectOpeningStock({
          productType: attempt.form.productType,
          defaultLocationId: attempt.defaultLocationId,
          stockChoice: attempt.stockChoice,
          simpleQty: attempt.simpleQty,
          productCost: num(attempt.form.costPrice),
          variants: attempt.variation.variants,
          locationRows: attempt.openingStock,
        }),
        idempotencyKey: key,
      });
    } catch (e) {
      /**
       * The RPC failed. That is not automatically "nothing was created": the
       * server may have committed and the response been lost, or two calls may
       * have raced and hit the unique backstop. Ask the server by key.
       *
       * F-01 (Version 373): write-ahead protection may only be cleared by a
       * failure that PROVES nothing committed, a lookup that positively found
       * nothing, and no other invocation that could still be running under the
       * same key. A codeless HTTP error proves none of that — an intermediary can
       * answer with a 4xx after the insert already committed.
       */
      const failure = classifyCreateFailure(e);
      /**
       * P1: there is ONE reconciliation policy, shared by every error basis.
       *
       * A local pre-flight rejection and a definitive server rejection both go
       * through the same decision, and the attempt epoch is read BEFORE that
       * decision, so an abandoned attempt can never reconcile another attempt's
       * record. Whether a record may be removed at all is then decided by the
       * centralised clearing policy, which retains a non-canonical creation
       * identity under every error basis.
       *
       * The lookup runs for every basis: a legacy key may already belong to a
       * product created by an earlier invocation, and adopting it is safer than
       * clearing the record that names it.
       */
      const attemptAbandoned = !attemptEpochIsCurrent(attemptEpochRef.current, epoch);
      const lookup = await lookupCreatedProduct(attempt.orgId, key);
      const decision = decideCreateReconciliation({
        kind: failure.kind,
        lookupProductId: lookup.productId,
        lookupOk: lookup.ok,
        sameKeyAttemptUnresolved: attemptAbandoned
          || (submissionRef.current.inFlight && submissionRef.current.idemKey !== key),
      });

      if (decision === "adopt_product" && lookup.productId) {
        if (attemptAbandoned) {
          // The product exists. Record it against the draft it was created for so
          // a later reload resumes its remaining steps — but never bind it to the
          // attempt that replaced this one.
          if (attempt.draftId) {
            persistResolvedRecovery({
              orgId: attempt.orgId,
              draftId: attempt.draftId,
              productId: lookup.productId,
              productName: attempt.form.name,
              // N1c: adoption keeps the identity that found the product. Replacing
              // it with a derived key would be a new creation identity.
              idemKey: key,
              stage: "canonical",
              // The wizard has already left this attempt, so its refusal notice
              // must not be written into the attempt that replaced it.
            }, { silent: true });
          }
          return;
        }
        rpcData = { productId: lookup.productId };
      } else if (decision === "clear_pending") {
        /**
         * Proven pre-commit rejection with a confirmed-empty lookup, so absence
         * is real. The centralised clearing policy still decides: a canonical
         * identity may be released (it is derivable again, so the next attempt
         * is the same create), a non-canonical one may not.
         */
        const cleared = attempt.draftId
          ? clearCreateRecoveryOwned(browserRecoveryStorage(), {
            orgId: attempt.orgId,
            draftId: attempt.draftId,
            attemptKey: key,
            attemptId: attemptIdRef.current,
          })
          : "none";
        if (cleared === "refused" && attemptEpochIsCurrent(attemptEpochRef.current, epoch)) {
          // The record was retained because its identity cannot be re-derived.
          // A failed invocation does not establish that another earlier
          // invocation with that key cannot commit, so the draft stays unresolved.
          setUnresolvedCreateKey(key);
          setRecoveryNotice(recoveryNoticeMessage("identity_retained"));
          unlockFailedCreate();
          setError(`${failure.message} This draft keeps its earlier creation identity, because an earlier attempt that used it may still be finishing. Check the Products page before assuming nothing was created.`);
          return;
        }
        if (attemptEpochIsCurrent(attemptEpochRef.current, epoch)) {
          setUnresolvedCreateKey(null);
          // Two different facts, two different messages: a local pre-flight
          // rejection never left the device, a definitive server rejection did
          // reach the server before it aborted.
          setRecoveryNotice(recoveryNoticeMessage(
            failure.basis === "local_validation" ? "validation_failure" : "rejected_before_commit",
          ));
        }
        unlockFailedCreate();
        setError(failure.message);
        return;
      } else if (attemptAbandoned) {
        // Retained on purpose: a create with this key may still be unresolved,
        // and this result belongs to an attempt the wizard has already left.
        return;
      } else if (lookup.ok) {
        // The lookup found nothing, but the failure does not prove the create
        // cannot still commit. Keep the pending record and the same key so a
        // retry is submitted as the same creation.
        if (attemptEpochIsCurrent(attemptEpochRef.current, epoch)) {
          setUnresolvedCreateKey(key);
          setRecoveryNotice(recoveryNoticeMessage("pending_unknown"));
        }
        unlockFailedCreate();
        setError(
          `The create did not report back, and an immediate check found no product yet. It may still be completing, so this draft keeps its creation identity and a retry is submitted as the same create. Whether the server collapses it into one product depends on the product-creation idempotency check being active for this key. Check the Products page before assuming nothing was created. ${failure.message}`,
        );
        return;
      } else {
        // The outcome is unknown. Keep the pending record and the same key.
        if (attemptEpochIsCurrent(attemptEpochRef.current, epoch)) {
          setUnresolvedCreateKey(key);
          setRecoveryNotice(recoveryNoticeMessage("pending_unknown"));
        }
        unlockFailedCreate();
        setError(`The create did not report back and could not be checked. This draft keeps its creation identity, so a retry is submitted as the same create — but confirm the outcome on the Products page before assuming nothing was created. ${failure.message}`);
        return;
      }
    }

    const productId = rpcData.productId;

    /**
     * F-02 (Version 372): the attempt was abandoned while the RPC was in flight.
     * The product genuinely exists, so keep the durable record for its draft —
     * but never bind it to the new attempt's lock, warning state or outcomes.
     */
    if (!attemptEpochIsCurrent(attemptEpochRef.current, epoch)) {
      if (productId && attempt.draftId) {
        persistResolvedRecovery({
          orgId: attempt.orgId,
          draftId: attempt.draftId,
          productId,
          productName: attempt.form.name,
          idemKey: key,
          stage: "canonical",
          // Silent: this attempt was abandoned, so its notice must not appear on
          // the attempt that replaced it.
        }, { silent: true });
      }
      return;
    }
    if (productId) submissionRef.current = bindCreatedProduct(submissionRef.current, productId);

    /**
     * F-01/D: upgrade the write-ahead record to the confirmed product
     * immediately, before any further post-create await. A reload from here on
     * resumes the remaining steps instead of creating again.
     */
    if (productId && attempt.draftId) {
      persistResolvedRecovery({
        orgId: attempt.orgId,
        draftId: attempt.draftId,
        productId,
        productName: attempt.form.name,
        idemKey: submissionRef.current.idemKey,
        stage: "canonical",
      });
    }

    const holdCreated = (step: "canonical" | "media", message: string) => {
      if (!attemptEpochIsCurrent(attemptEpochRef.current, epoch)) return;
      submissionRef.current = pauseAfterCreate(submissionRef.current);
      setSaving(false);
      setPostCreateHold({ productId: productId!, name: attempt.form.name, step, message });
      // B-008: this product exists. Record that against the draft so a reload
      // restores the pending steps instead of an empty wizard that could create
      // the same product a second time.
      if (attempt.draftId) {
        persistResolvedRecovery({
          orgId: attempt.orgId,
          draftId: attempt.draftId,
          productId: productId!,
          productName: attempt.form.name,
          idemKey: submissionRef.current.idemKey,
          stage: step,
        });
      } else {
        setRecoveryNotice("This product exists, but the wizard has no saved draft, so this recovery cannot be resumed after a reload. Finish the remaining steps on the product page.");
      }
    };

    if (productId) {
      try {
        await productApi.applyCanonicalProductData(attempt.orgId, productId, {
          manufacturer: attempt.form.manufacturer || null,
          model_number: attempt.form.modelNumber || null,
          mpn: attempt.form.mpn || null,
          condition: attempt.form.condition || null,
          country_of_origin: attempt.form.countryOfOrigin || null,
          unit_of_measure: attempt.form.unit || null,
          seo_keywords: attempt.form.seoKeywords || null,
          url_slug: displayedUrlSlug(attempt.form) || null,
          identifiers_not_applicable: attempt.form.identifiersNotApplicable,
          gtin: attempt.form.identifiersNotApplicable ? null : attempt.form.gtin || null,
          upc: attempt.form.identifiersNotApplicable ? null : attempt.form.upc || null,
          ean: attempt.form.identifiersNotApplicable ? null : attempt.form.ean || null,
          material: attempt.form.material || null,
        });
      } catch (e) {
        holdCreated("canonical", `This product already exists. Canonical details were not saved, and Create will not run again for it. ${(e as Error).message}`);
        return;
      }
    }

    if (productId && attempt.draftId && attempt.hasMedia) {
      try {
        /**
         * PHASE1-CREATED-PRODUCT-READINESS-001: a resolved promise is not a
         * transfer. A business-level `ok: false` used to fall through to the
         * success path, which then discarded the draft — the only copy of the
         * images. The verdict is what decides whether the create may settle.
         */
        const transfer = await transferWizardDraftMedia(attempt.orgId, attempt.draftId, productId);
        const verdict = classifyDraftMediaTransfer(transfer, attempt.mediaAssetIds.length);
        if (!verdict.ok) throw new Error(verdict.message ?? "The images were not transferred to the product.");
      } catch (e) {
        const nextFailures = upsertMediaFailure(mediaFailuresRef.current, {
          id: draftTransferFailureId(attempt.draftId),
          name: "draft images",
          reason: (e as Error).message || "Transfer failed",
          source: "draft_transfer",
        });
        mediaFailuresRef.current = nextFailures;
        setMediaFailures(nextFailures);
        holdCreated("media", `This product already exists, but images are still on the draft. Create will not run again for it. ${(e as Error).message}`);
        return;
      }
    }

    // Link the supplier through the canonical supplier–product relationship.
    // B-003: this is one shared step used by create AND retry, so recovery can
    // never skip it. The service upserts on the natural key, so a retry is safe.
    const supplierLinked = productId ? await runSupplierLink(attempt, productId, epoch) : false;

    queryClient.invalidateQueries({ queryKey: ["products", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
    if (!productId) {
      /**
       * F-01 (Version 373): a successful RPC response that carries no product id
       * does NOT confirm that a product was created. Re-persist the write-ahead
       * record (so the claim below is literally true) and tell the operator only
       * what is actually known.
       */
      const retainedKey = submissionRef.current.idemKey;
      const retainedWrite = attempt.draftId
        ? writePendingCreateRecovery(browserRecoveryStorage(), {
          orgId: attempt.orgId,
          draftId: attempt.draftId,
          productName: attempt.form.name,
          idemKey: retainedKey,
          attemptId: attemptIdRef.current,
        })
        : "unavailable";
      const retained = retainedWrite === "written"
        && confirmCreateRecoveryPersisted(browserRecoveryStorage(), {
          orgId: attempt.orgId,
          draftId: attempt.draftId as string,
          idemKey: retainedKey,
        });
      if (attemptEpochIsCurrent(attemptEpochRef.current, epoch)) {
        setUnresolvedCreateKey(retained || retainedWrite === "unreadable" || retainedWrite === "conflict"
          ? retainedKey
          : null);
        if (retainedWrite === "unreadable") setRecoveryUnreadable(true);
        setRecoveryNotice(recoveryNoticeMessage(
          retained
            ? "pending_unknown"
            : retainedWrite === "unreadable"
              ? "recovery_unreadable"
              : retainedWrite === "conflict"
                ? "recovery_write_conflict"
                : "storage_unavailable",
        ));
      }
      unlockFailedCreate();
      setError(retained
        ? "The create did not come back with a confirmed product, so its outcome is unconfirmed. This draft keeps its creation identity, so a retry is submitted as the same create. Whether the server collapses it into one product depends on the product-creation idempotency check. Check the Products page before assuming anything was created."
        : "The create did not come back with a confirmed product, so its outcome is unconfirmed. This browser could not store the recovery record, so a reload cannot resume it — check the Products page before creating again.");
      return;
    }
    // Abandoned while the supplier/media steps ran: do not start channel writes
    // for an attempt the wizard no longer owns.
    if (!attemptEpochIsCurrent(attemptEpochRef.current, epoch)) return;
    const variantCount = Number(rpcData.variantCount) || 1;
    const stockRows = createdStockRows(attempt);
    const nextOutcomes = await runChannelDrafts(attempt, productId, []);
    if (!commitAttemptResult(attemptEpochRef.current, epoch, nextOutcomes, (value) => {
      channelOutcomesRef.current = value;
    })) return;
    const identity = successIdentity({ name: attempt.form.name, sku: rpcData.sku ?? null });
    setCreated({
      productId,
      name: identity.name,
      productType: attempt.form.productType,
      variantCount,
      sku: identity.sku,
      openingStock: stockRows,
      channelOutcomes: channelOutcomesRef.current,
    });
    setPostCreateHold(null);
    setUnresolvedCreateKey(null);
    setRecoveryNotice("");
    // The attempt is finished as a *create*. Whether the draft is discarded is
    // decided by settleCreatedAttempt: an unresolved channel step or supplier
    // link keeps both the draft and a recovery record, so a reload cannot start
    // a second product for this attempt.
    submissionRef.current = finishSubmission();
    setSaving(false);
    void settleCreatedAttempt({
      attempt,
      productId,
      outcomes: nextOutcomes,
      supplierLinked,
      mediaFailures: unresolvedMediaFailureCount(mediaFailuresRef.current),
      epoch,
    });
  };

  const retryPostCreate = async () => {
    const hold = postCreateHold;
    if (!hold) return;
    const epoch = attemptEpochRef.current.value;
    // B-009: the same creation attempt must never be mutated by two retries at
    // once. The gate is released on success, error and thrown exception.
    const outcome = await runGuardedRetry(retryGuardRef.current, submissionRef.current, async () => {
      // A retry restored from a draft has no in-memory snapshot; rebuild it from
      // the draft the wizard just hydrated.
      const rebuilt = attemptSnapshotRef.current ?? buildAttemptSnapshot();
      if (!rebuilt) {
        setRecoveryNotice("The saved draft could not be read, so the remaining steps cannot be retried here. Finish them on the product page.");
        return;
      }
      attemptSnapshotRef.current = rebuilt;
      const attempt = rebuilt;
      const rebuild = canRebuildAttemptFromDraft({
        stage: hold.step,
        hasForm: attempt.form.name.trim().length > 0,
        hasDraftMedia: attempt.hasMedia,
      });
      if (!rebuild.ok) {
        if (attemptEpochIsCurrent(attemptEpochRef.current, epoch)) setRecoveryNotice(rebuild.reason ?? "");
        return;
      }
      submissionRef.current = { ...submissionRef.current, inFlight: true };
      setSaving(true);
      setRecoveryNotice("");
      try {
      if (hold.step === "canonical") {
        await productApi.applyCanonicalProductData(attempt.orgId, hold.productId, {
          manufacturer: attempt.form.manufacturer || null,
          model_number: attempt.form.modelNumber || null,
          mpn: attempt.form.mpn || null,
          condition: attempt.form.condition || null,
          country_of_origin: attempt.form.countryOfOrigin || null,
          unit_of_measure: attempt.form.unit || null,
          seo_keywords: attempt.form.seoKeywords || null,
          url_slug: displayedUrlSlug(attempt.form) || null,
          identifiers_not_applicable: attempt.form.identifiersNotApplicable,
          gtin: attempt.form.identifiersNotApplicable ? null : attempt.form.gtin || null,
          upc: attempt.form.identifiersNotApplicable ? null : attempt.form.upc || null,
          ean: attempt.form.identifiersNotApplicable ? null : attempt.form.ean || null,
          material: attempt.form.material || null,
        });
      }
      /**
       * F-01 (Version 372): a restored `media` record resumes the transfer. An
       * empty in-memory failure list is NOT evidence that the images moved, so
       * the transfer has to actually run — otherwise the hold is kept.
       */
      let mediaUnresolved = false;
      if (hold.step !== "post_create") {
        if (!attempt.draftId || !attempt.hasMedia) {
          mediaUnresolved = hold.step === "media";
        } else {
          try {
            const transfer = await transferWizardDraftMedia(attempt.orgId, attempt.draftId, hold.productId);
            const verdict = classifyDraftMediaTransfer(transfer, attempt.mediaAssetIds.length);
            if (!verdict.ok) throw new Error(verdict.message ?? "The images were not transferred to the product.");
            // Repair 4: the transfer that just succeeded is the ONLY failure this
            // retry resolves. A channel-media warning is a different obligation and
            // must stay visible.
            const remainingFailures = resolveDraftTransferFailures(mediaFailuresRef.current, attempt.draftId);
            if (remainingFailures !== mediaFailuresRef.current) {
              mediaFailuresRef.current = remainingFailures;
              setMediaFailures(remainingFailures);
            }
          } catch (e) {
            const nextFailures = upsertMediaFailure(mediaFailuresRef.current, {
              id: draftTransferFailureId(attempt.draftId),
              name: "draft images",
              reason: (e as Error).message || "Transfer failed",
              source: "draft_transfer",
            });
            // A late failure from an abandoned attempt must not touch the new one.
            if (attemptEpochIsCurrent(attemptEpochRef.current, epoch)) {
              mediaFailuresRef.current = nextFailures;
              setMediaFailures(nextFailures);
              submissionRef.current = pauseAfterCreate(submissionRef.current);
              setSaving(false);
              setPostCreateHold({
                ...hold,
                step: "media",
                message: `This product already exists, but its images are still on the draft. ${(e as Error).message}`,
              });
            }
            return;
          }
        }
      }
      // The requested channels were never written when the create path stopped
      // early, so finish them here. Without this, a retry reported a clean
      // success while no draft listing (and no channel primary image) existed.
      // B-003: a retry must finish the supplier link too, not just media and channels.
      // The linked step upserts, so a link that already exists is never duplicated.
      let supplierLinked = supplierLinkedRef.current;
      if (!supplierLinked) supplierLinked = await runSupplierLink(attempt, hold.productId, epoch);
      if (!attemptEpochIsCurrent(attemptEpochRef.current, epoch)) return;
      // F-02: capture the result locally and commit it only if this attempt is
      // still current. Assigning the awaited value straight into the ref let a
      // late callback from an abandoned attempt overwrite a new product's
      // outcomes — and a later retry would then reuse the old listing id.
      const nextOutcomes = await runChannelDrafts(attempt, hold.productId, channelOutcomesRef.current);
      if (!commitAttemptResult(attemptEpochRef.current, epoch, nextOutcomes, (value) => {
        channelOutcomesRef.current = value;
      })) return;
      if (mediaUnresolved) {
        // The record said the images still had to move, and nothing could move
        // them. Keep the media step outstanding — never settle as if it worked.
        submissionRef.current = pauseAfterCreate(submissionRef.current);
        setSaving(false);
        setPostCreateHold({
          ...hold,
          step: "media",
          message: "This product already exists, but the draft images are no longer available, so they could not be transferred. Add them again from the product's Media tab.",
        });
        return;
      }
      const identity = successIdentity({ name: attempt.form.name, sku: attempt.form.sku || null });
      setPostCreateHold(null);
      setCreated({
        productId: hold.productId,
        name: identity.name,
        productType: attempt.form.productType,
        variantCount: attempt.form.productType === "variation" ? attempt.variation.variants.length : 1,
        sku: identity.sku,
        openingStock: createdStockRows(attempt),
        channelOutcomes: channelOutcomesRef.current,
      });
      submissionRef.current = finishSubmission();
      setSaving(false);
      void settleCreatedAttempt({
        attempt,
        productId: hold.productId,
        outcomes: nextOutcomes,
        supplierLinked,
        mediaFailures: unresolvedMediaFailureCount(mediaFailuresRef.current),
        epoch,
      });
      } catch (e) {
        // A late callback from an abandoned attempt must not touch the new one.
        if (!attemptEpochIsCurrent(attemptEpochRef.current, epoch)) return;
        submissionRef.current = pauseAfterCreate(submissionRef.current);
        setSaving(false);
        setPostCreateHold({ ...hold, message: `This product already exists. The retry did not finish. ${(e as Error).message}` });
      }
    });
    if (outcome === "skipped") {
      setError("Another recovery step is already running for this product. Let it finish, then retry.");
    }
  };

  /**
   * Retry the supplier link (B-003) under the shared attempt guard (B-009).
   *
   * `retryPostCreate` and `retryChannels` already shared the gate, but this path
   * did not, so a supplier retry could mutate the same creation attempt
   * concurrently with a channel or post-create retry. The gate is released in
   * `finally`, so success, a rejected operation and a thrown exception all
   * unlock the attempt.
   */
  const retrySupplierLink = async () => {
    if (!retryLink) return;
    const epoch = attemptEpochRef.current.value;
    const link = retryLink;
    const outcome = await runGuardedRetry(retryGuardRef.current, submissionRef.current, async () => {
      setRetryingLink(true);
      setError("");
      try {
        await linkSupplierProduct(org!.id, { ...link, variantId: null });
        // F-02: validate ownership BEFORE touching shared attempt state. The
        // remote link is already committed and must not be "cancelled" — only
        // its late result is prevented from contaminating another attempt.
        if (!attemptEpochIsCurrent(attemptEpochRef.current, epoch)) return;
        // The link now exists, so no later retry may attempt it again.
        supplierLinkedRef.current = true;
        setSupplierLinkFailed(false);
        setSupplierLinkName("");
        setRetryLink(null);
        const attempt = attemptSnapshotRef.current;
        const productId = created?.productId ?? submissionRef.current.productId;
        if (attempt && productId) {
          void settleCreatedAttempt({
            attempt,
            productId,
            outcomes: channelOutcomesRef.current,
            supplierLinked: true,
            mediaFailures: unresolvedMediaFailureCount(mediaFailuresRef.current),
            epoch,
          });
        }
      } catch (e) {
        // Keep the failed state; the operator can retry again or fix it on the product page.
        if (attemptEpochIsCurrent(attemptEpochRef.current, epoch)) {
          setError((e as Error).message || "The supplier link still could not be saved.");
        }
      } finally {
        if (attemptEpochIsCurrent(attemptEpochRef.current, epoch)) setRetryingLink(false);
      }
    });
    if (outcome === "skipped") {
      setError("Another recovery step is already running for this product. Let it finish, then retry the supplier link.");
    }
  };

  /**
   * B-007: replay only the channel steps that are still unresolved for this
   * attempt. Completion is tracked per connector + stage, so accounts that
   * already produced a listing (and their primary image) are never touched
   * again, and a media-only failure retries just the image apply.
   *
   * B-009: the same attempt must never be mutated by two retries at once, so
   * this path shares the guard with the post-create retry.
   */
  const retryChannels = async () => {
    const epoch = attemptEpochRef.current.value;
    const outcome = await runGuardedRetry(retryGuardRef.current, submissionRef.current, async () => {
      const attempt = attemptSnapshotRef.current;
      const productId = created?.productId ?? submissionRef.current.productId;
      if (!attempt || !productId) return;
      setRetryingChannels(true);
      setError("");
      try {
        // F-02: same rule as the post-create retry — never assign an awaited
        // result into the shared ref before the attempt epoch is validated.
        const nextOutcomes = await runChannelDrafts(attempt, productId, channelOutcomesRef.current);
        const committed = commitAttemptResult(attemptEpochRef.current, epoch, nextOutcomes, (value) => {
          channelOutcomesRef.current = value;
          setCreated((prev) => (prev ? { ...prev, channelOutcomes: value } : prev));
        });
        if (!committed) return;
        // Repair 4: channel-media outcomes live in `channelOutcomes`, and the
        // merge replaces a resolved failure with a success so its warning row
        // disappears. Draft-media transfer failures are a separate obligation —
        // a channel retry does not move draft images, so they are NOT cleared.
        void settleCreatedAttempt({
          attempt,
          productId,
          outcomes: nextOutcomes,
          supplierLinked: supplierLinkedRef.current,
          mediaFailures: unresolvedMediaFailureCount(mediaFailuresRef.current),
          epoch,
        });
      } catch (e) {
        if (!attemptEpochIsCurrent(attemptEpochRef.current, epoch)) return;
        setError((e as Error).message || "The channel retry did not finish.");
      } finally {
        if (attemptEpochIsCurrent(attemptEpochRef.current, epoch)) setRetryingChannels(false);
      }
    });
    if (outcome === "skipped") {
      setError("Another recovery step is already running for this product. Let it finish, then retry the channel steps.");
    }
  };

  /**
   * Start a new product. Only state owned by the previous attempt is cleared.
   *
   * Two things must survive or be handled here:
   *  - an attempt abandoned mid-flight bumps the epoch, so its late callbacks
   *    cannot write into the new product;
   *  - a draft that still carries unresolved recovery is left on the server and
   *    keeps its recovery record, so it can still be finished later. It is not
   *    deleted just because the operator chose to start something else.
   */
  const reset = () => {
    nextAttemptEpoch(attemptEpochRef.current);
    // F-07: the retry gate is released ONLY by the operation that owns it (in
    // its own finally). Clearing it here let an abandoned attempt release a new
    // attempt's lock. The epoch bump is what invalidates the old callbacks.
    supplierLinkedRef.current = false;
    channelOutcomesRef.current = [];
    attemptSnapshotRef.current = null;
    // Detach the wizard from the previous draft so no queued or late save can
    // adopt it into the new, blank product.
    syncDraftOwner(org?.id ?? null, null, true);
    setStep(0);
    setForm(initialForm);
    setVariation({ options: [], variants: [], combinationCount: 0, duplicateSkus: [], hasData: false });
    setDraftMedia([]);
    setOpeningStock([]);
    setStockChoice("none");
    setCreated(null);
    setPostCreateHold(null);
    setRecoveryNotice("");
    setRecoveryWriteNotice("");
    setUnresolvedCreateKey(null);
    setRecoveryUnreadable(false);
    attemptCreateKeyRef.current = "";
    submissionRef.current = finishSubmission();
    setSaving(false);
    setError("");
    setEnrichDraft(null);
    setEnrichError("");
    setSupplierLinkFailed(false);
    setSupplierLinkName("");
    setRetryLink(null);
    setRetryingLink(false);
    mediaFailuresRef.current = [];
    setMediaFailures([]);
    setRetryingChannels(false);
    setSellingIntents([]);
    setSimpleQty("");
    setMatrixLocationId("");
    setStockByLocation(false);
    setDraftId(null);
    setSearchParams({}, { replace: true });
    setVariantEditorKey("new");
    setMediaNotice("");
    setDraftSaveStatus("idle");
    setDraftSaveError("");
    setDraftHydrationReady(true);
  };

  const handleEnrich = async () => {
    setEnrichError("");
    setEnriching(true);
    try {
      const draft = await enrichProduct(
        {
          name: form.name,
          brand: form.brand || null,
          categoryName: (data?.categories ?? []).find((c) => c.id === form.categoryId)?.name ?? null,
          productType: form.productType,
          optionNames: form.productType === "variation" ? variation.options.map((o) => o.name) : [],
          optionValues: form.productType === "variation" ? variation.options.flatMap((o) => o.values.map((v) => v.value)) : [],
          optionGroups: form.productType === "variation"
            ? Object.fromEntries(variation.options.map((o) => [o.name, o.values.map((v) => v.value)]))
            : {},
        },
        org!.id,
      );
      setEnrichDraft(draft);
    } catch (e) {
      setEnrichError((e as Error).message);
    } finally {
      setEnriching(false);
    }
  };

  const applyEnrichDraft = (overwriteConfirmed = false) => {
    if (!enrichDraft) return;
    const current = {
      description: form.description,
      shortDescription: form.shortDescription,
      seoTitle: form.seoTitle,
      metaDescription: form.metaDescription,
      seoKeywords: form.seoKeywords,
    };
    const draft = {
      description: enrichDraft.description,
      shortDescription: enrichDraft.shortDescription,
      seoTitle: enrichDraft.seoTitle,
      metaDescription: enrichDraft.metaDescription,
      seoKeywords: enrichDraft.seoKeywords ?? enrichDraft.tags.join(", "),
    };
    if (!overwriteConfirmed && enrichmentNeedsOverwriteConfirm(current, draft)) {
      const ok = window.confirm("Some fields already have your text. Replace them with the AI draft?");
      if (!ok) {
        const merged = mergeEnrichmentFields(current, draft, false);
        setForm((f) => ({ ...f, ...merged, tags: f.tags.trim() ? f.tags : enrichDraft.tags.join(", ") }));
        return;
      }
    }
    const merged = mergeEnrichmentFields(current, draft, true);
    setForm((f) => ({ ...f, ...merged, tags: enrichDraft.tags.join(", ") }));
  };

  const discardEnrichDraft = () => {
    setEnrichDraft(null);
    setEnrichError("");
  };

  const handleQuickAddCategory = async () => {
    setCatQuickError("");
    const name = newCategoryName.trim();
    if (!name) {
      setCatQuickError("Enter a category name.");
      return;
    }
    // Friendly duplicate check before writing (the DB also enforces per org/parent).
    const dup = findDuplicateCategory(data?.categories ?? [], name, null);
    if (dup) {
      setCatQuickError(
        dup.archived
          ? `A category named "${dup.name}" already exists but is archived. Restore it from the Categories list instead.`
          : `A category named "${dup.name}" already exists — select it from the list instead.`,
      );
      return;
    }
    setCatQuickSaving(true);
    const { data: catData, error: catErr } = await supabase
      .from("categories")
      .insert({ org_id: org!.id, name })
      .select("id")
      .maybeSingle();
    setCatQuickSaving(false);
    if (catErr) {
      setCatQuickError(catErr.message);
      return;
    }
    if (catData?.id) {
      set("categoryId", catData.id as string);
      setNewCategoryName("");
      setCatQuickAdd(false);
      queryClient.invalidateQueries({ queryKey: ["create-product-refs", org?.id] });
    }
  };

  if (postCreateHold) {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-slate-200 p-10">
        <h2 className="text-2xl font-semibold text-slate-900">Product already created</h2>
        <p className="mt-2 text-slate-700">{postCreateHold.name}</p>
        <p className="mt-3 text-sm text-amber-800">{postCreateHold.message}</p>
        {recoveryNotice && (
          <p className="mt-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">{recoveryNotice}</p>
        )}
        {error && (
          <p className="mt-2 px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700">{error}</p>
        )}
        <div className="mt-6 flex flex-wrap gap-2">
          <button type="button" onClick={() => void retryPostCreate()} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg">
            {saving ? "Retrying..." : "Retry this step"}
          </button>
          <button type="button" onClick={() => navigate(`/products/${postCreateHold.productId}`)} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-lg">
            Open product
          </button>
        </div>
      </div>
    );
  }

  if (created) {
    return (
      <SuccessScreen
        result={created}
        onAddAnother={reset}
        supplierWarning={supplierLinkFailed ? supplierLinkName : null}
        onRetrySupplier={retryLink ? retrySupplierLink : undefined}
        retryingSupplier={retryingLink}
        mediaFailures={mediaFailures}
        notice={error || recoveryWriteNotice || null}
        onRetryChannels={retryChannels}
        retryingChannels={retryingChannels}
      />
    );
  }

  const categoryName = (data?.categories ?? []).find((c) => c.id === form.categoryId)?.name;
  const supplierName = (data?.suppliers ?? []).find((s) => s.id === form.supplier.supplierId)?.name;
  const projectedStock = projectOpeningStock({
    productType: form.productType,
    defaultLocationId,
    stockChoice,
    simpleQty,
    productCost: num(form.costPrice),
    variants: variation.variants,
    locationRows: openingStock,
  });
  const projectedStockTotal = openingStockTotal(projectedStock);
  const openingQtyWarnings = [
    form.productType === "simple" ? visibleOpeningQuantityError(simpleQty) : null,
    ...variation.variants.map((variant) => visibleOpeningQuantityError(variant.opening_qty)).filter((message): message is string => !!message),
    ...openingStock.map((row) => visibleOpeningQuantityError(row.qty)).filter((message): message is string => !!message),
  ].filter((message): message is string => !!message);

  return (
    <div>
      <PageHeader title="Create Product" subtitle="Enter the product once. Tradivo checks selected channels before you publish." backTo="/products">
        {draftId && (
          unresolvedCreateKey || recoveryUnreadable ? (
            <button
              type="button"
              onClick={() => navigate("/products")}
              className="px-3 py-2 bg-white border border-amber-200 hover:bg-amber-50 text-amber-800 text-sm font-medium rounded-lg whitespace-nowrap"
              title="Leave this draft without deleting it or its creation identity"
            >
              Leave draft
            </button>
          ) : (
            <button type="button" onClick={() => void discardDraft()} disabled={!canDiscardDraft(submissionRef.current) || saving || discardingDraft || mediaBusy || recoveryUnreadable} className="px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-40 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              {discardingDraft ? "Discarding draft..." : "Discard draft"}
            </button>
          )
        )}
        {draftSaveStatus === "saving" && <span className="text-xs text-slate-500">Saving...</span>}
        {draftSaveStatus === "saved" && <span className="text-xs text-emerald-700">Saved</span>}
        {draftSaveStatus === "error" && <span className="text-xs text-red-600">{draftSaveError || "Save failed"}</span>}
      </PageHeader>

      {recoveryNotice && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-100 rounded-lg text-sm text-amber-800" role="status">
          {recoveryNotice}
        </div>
      )}

      {/* Stepper */}
      <div className="mb-6 flex items-center gap-1 overflow-x-auto">
        {steps.map((s, i) => (
          <div key={s.key} className="flex items-center">
            <button
              onClick={() => { if (!saving && i <= step) setStep(i); }}
              disabled={saving}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                i === step ? "bg-emerald-600 text-white" : i < step ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-slate-100 text-slate-400"
              }`}
            >
              <i className={s.icon} />
              <span>{i + 1}. {s.label}</span>
            </button>
            {i < steps.length - 1 && <span className="text-slate-300 mx-1"><i className="ri-arrow-right-s-line" /></span>}
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        {error && <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}

        {/* STEP 0 — Product */}
        {step === 0 && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {([
              { key: "simple", title: "Simple Product", desc: "One sellable SKU. Name, SKU, price, and you're done.", icon: "ri-price-tag-3-line" },
              { key: "variation", title: "Product with Variations", desc: "Multiple SKUs, like Colour × Size. Build options, we generate the combinations.", icon: "ri-grid-line" },
              { key: "bundle", title: "Bundle / Kit", desc: "A product assembled from other SKUs.", icon: "ri-box-3-line" },
            ] as { key: ProductType; title: string; desc: string; icon: string }[]).map((t) => (
              <button
                key={t.key}
                onClick={() => set("productType", t.key)}
                className={`p-5 rounded-xl border-2 text-left transition-colors cursor-pointer ${
                  form.productType === t.key ? "border-emerald-600 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"
                }`}
              >
                <i className={`${t.icon} text-2xl ${form.productType === t.key ? "text-emerald-600" : "text-slate-400"}`} />
                <h3 className="mt-3 text-sm font-semibold text-slate-800">{t.title}</h3>
                <p className="mt-1 text-xs text-slate-500">{t.desc}</p>
              </button>
            ))}
            </div>
            {!org && <LoadingState />}
            {org && (
              <>
            <p className="text-sm text-slate-500">Fields marked <span className="font-semibold">*</span> are required to create the Tradivo product. Marketplace-only requirements stay optional here.</p>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Essential</p>
            <div>
              <label className={labelCls} htmlFor="product-name">Product name *</label>
              <input
                id="product-name"
                value={form.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setForm((f) => ({ ...f, name, sku: f.sku.trim() === "" ? suggestSku(name) : f.sku }));
                }}
                className={inputCls}
                placeholder="e.g. Running T-Shirt"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls} htmlFor="internal-reference">Internal reference / base SKU <span className="text-slate-400 font-normal">(optional)</span></label>
                <input
                  id="internal-reference"
                  value={form.reference}
                  onChange={(e) => set("reference", e.target.value)}
                  className={inputCls}
                  placeholder="TSHIRT-001"
                />
                <p className="text-xs text-slate-400 mt-1">Your catalogue code. {form.productType === "variation" ? "Used as the base for variant SKUs." : "Optional — different from the sellable SKU."}</p>
              </div>
              {form.productType !== "variation" ? (
                <div>
                  <label className={labelCls} htmlFor="sku">SKU *</label>
                  <input id="sku" value={form.sku} onChange={(e) => set("sku", e.target.value)} className={inputCls} placeholder="RT-001" />
                  <p className="text-xs text-slate-400 mt-1">Auto-filled from the name — edit if needed. This is the code you'll scan and search by.</p>
                  {form.sku.trim() === "" ? (
                    <p className="text-xs text-red-500 mt-1">A SKU is required. It auto-fills from the name, or type your own.</p>
                  ) : skuValidationError(form.sku) ? (
                    <p className="text-xs text-red-500 mt-1">{skuValidationError(form.sku)}</p>
                  ) : (
                    <p className="text-xs text-slate-400 mt-1">Marketplace-safe: A-Z, 0-9 and hyphens, maximum 40 characters.</p>
                  )}
                </div>
              ) : (
                <div className="flex items-start gap-2 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-500">
                  <i className="ri-grid-line mt-0.5 text-slate-400" />
                  <span>Each variant gets its own SKU, generated automatically in the next step from the internal reference above.</span>
                </div>
              )}
              <div>
                  <label className={labelCls} htmlFor="category">Category <span className="text-slate-400 font-normal">(optional)</span></label>
                <select id="category" value={form.categoryId} onChange={(e) => set("categoryId", e.target.value)} className={inputCls}>
                  <option value="">No category</option>
                  {flatCategories.map((c) => <option key={c.id} value={c.id}>{"\u00A0".repeat(c.depth * 2)}{c.name}</option>)}
                </select>
                {!catQuickAdd ? (
                  <button
                    type="button"
                    onClick={() => { setCatQuickError(""); setCatQuickAdd(true); }}
                    className="mt-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 whitespace-nowrap"
                  >
                    <i className="ri-add-line" /> New category
                  </button>
                ) : (
                  <div className="mt-1 flex gap-1.5">
                    <input
                      autoFocus
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleQuickAddCategory(); } }}
                      className={inputCls}
                      placeholder="e.g. T-Shirts"
                    />
                    <button type="button" onClick={handleQuickAddCategory} disabled={catQuickSaving} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap">
                      {catQuickSaving ? "Adding..." : "Add"}
                    </button>
                  </div>
                )}
                {catQuickError && <p className="text-xs text-red-500 mt-1">{catQuickError}</p>}
                {flatCategories.length === 0 && !catQuickAdd && (
                  <p className="text-xs text-amber-600 mt-1">No categories yet — add one here, or leave it uncategorised for now.</p>
                )}
              </div>
              <MoneyInput
                id="selling-price"
                label={form.productType === "variation" ? "Price (default for variants)" : "Selling price"}
                value={form.sellingPrice}
                onChange={(raw) => set("sellingPrice", raw)}
                placeholder="24.95"
              />
              <MoneyInput
                id="cost-price"
                label={form.productType === "variation" ? "Cost (default for variants)" : "Cost price"}
                value={form.costPrice}
                onChange={(raw) => set("costPrice", raw)}
                placeholder="18.00"
              />
            </div>

            <div className="border-t border-slate-100 pt-4">
              <SupplierSection
                orgId={org.id}
                value={form.supplier}
                costPrice={form.costPrice}
                onChange={(supplier) => set("supplier", supplier)}
              />
              <p className="text-xs text-slate-400 mt-2">Cost and supplier stay internal to Tradivo. They do not make a product eligible for any marketplace.</p>
            </div>

            {form.productType === "variation" && (
              <p className="text-xs text-slate-400">Price and cost here become the defaults in the variant matrix — you can override any SKU.</p>
            )}
              </>
            )}
          </div>
        )}

        {/* STEP 1 — Stock (simple) */}
        {step === 1 && form.productType === "simple" && (
          <div className="space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Opening quantity</h3>
              <p className="text-xs text-slate-500 mt-1">Enter quantity for your default location. Use stock by location only if you have multiple warehouses.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setStockChoice("none")}
                className={`p-4 rounded-xl border-2 text-left transition-colors cursor-pointer ${stockChoice === "none" ? "border-emerald-600 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"}`}
              >
                <i className={`ri-checkbox-blank-circle-line text-xl ${stockChoice === "none" ? "text-emerald-600" : "text-slate-400"}`} />
                <h4 className="mt-2 text-sm font-semibold text-slate-800">No stock yet</h4>
                <p className="mt-1 text-xs text-slate-500">Start at zero — count it later or place a purchase order.</p>
              </button>
              <div className={`p-4 rounded-xl border-2 ${stockChoice === "record" || Number(simpleQty) > 0 ? "border-emerald-600 bg-emerald-50" : "border-slate-200"}`}>
                <h4 className="text-sm font-semibold text-slate-800">Quantity on hand</h4>
                {(data?.locations ?? []).length === 1 ? (
                  <div className="mt-3">
                    <label className={labelCls}>Qty at {(data?.locations ?? [])[0]?.name}</label>
                    <input type="number" min="0" value={simpleQty} onChange={(e) => { setSimpleQty(e.target.value); setStockChoice(Number(e.target.value) > 0 ? "record" : stockChoice); }} className={inputCls} placeholder="0" />
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-slate-500">Use stock by location below.</p>
                )}
              </div>
            </div>
            <button type="button" onClick={() => setStockByLocation((v) => !v)} className="text-xs font-medium text-emerald-600 hover:text-emerald-700">
              Stock by location
            </button>
            {(stockByLocation || (data?.locations ?? []).length > 1) && (
              <div className="border-t border-slate-100 pt-4">
                <p className="text-xs font-semibold text-slate-600 mb-2">Opening stock</p>
                {(data?.locations ?? []).length === 0 ? (
                  <div className="px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-700">
                    <i className="ri-map-pin-line mr-1" />
                    No locations yet — create one on the Locations page before stock can be placed.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {openingStock.map((row, i) => (
                      <div key={i} className="grid grid-cols-1 gap-2 items-center sm:grid-cols-[1fr_100px_110px_32px]">
                        <select value={row.location_id} onChange={(e) => updateOpeningStockRow(i, "location_id", e.target.value)} className={inputCls}>
                          <option value="">Select location...</option>
                          {(data?.locations ?? []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                        </select>
                        <input type="number" min="0" value={row.qty} onChange={(e) => updateOpeningStockRow(i, "qty", e.target.value)} className={inputCls} placeholder="Qty" />
                        <input type="number" step="0.01" min="0" value={row.unit_cost} onChange={(e) => updateOpeningStockRow(i, "unit_cost", e.target.value)} className={inputCls} placeholder="Unit cost" />
                        <button type="button" onClick={() => removeOpeningStockRow(i)} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500" aria-label="Remove location">
                          <i className="ri-close-line" />
                        </button>
                      </div>
                    ))}
                    <button type="button" onClick={addOpeningStockRow} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-add-line" /> Add another location
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Restock reminders (optional) */}
            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs font-semibold text-slate-600 mb-2">Restock reminders <span className="text-slate-400 font-normal">(optional)</span></p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Reorder point — when stock drops to</label>
                  <input type="number" min="0" value={form.reorderPoint} onChange={(e) => set("reorderPoint", e.target.value)} className={inputCls} placeholder="e.g. 10" />
                </div>
                <div>
                  <label className={labelCls}>Reorder quantity — order up to</label>
                  <input type="number" min="0" value={form.reorderQty} onChange={(e) => set("reorderQty", e.target.value)} className={inputCls} placeholder="e.g. 50" />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                <i className="ri-information-line mr-1" />
                Setting these lets Replenishment Planning warn you when it's time to reorder. Leave at 0 to manage stock manually.
              </p>
            </div>
          </div>
        )}

        {/* STEP 2 — Options (variation) — stay mounted so Step 3→4→Back keeps generated combinations */}
        <div className={step === 1 && form.productType === "variation" ? "space-y-5" : "hidden"}>
            {(data?.locations ?? []).length > 1 && (
              <div>
                <label className={labelCls} htmlFor="matrix-location">Location for matrix Qty</label>
                <select
                  id="matrix-location"
                  value={matrixLocationId}
                  onChange={(e) => setMatrixLocationId(e.target.value)}
                  className={inputCls}
                >
                  <option value="">Choose a location</option>
                  {(data?.locations ?? []).map((location) => (
                    <option key={location.id} value={location.id}>{location.name}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  {matrixLocationName
                    ? `Matrix quantities will be placed at ${matrixLocationName}.`
                    : "Matrix quantities stay unplaced until you choose a location. Stock by location is separate."}
                </p>
              </div>
            )}
            {(data?.locations ?? []).length === 1 && (
              <p className="text-xs text-slate-500">Matrix quantities will be placed at {(data?.locations ?? [])[0]?.name}.</p>
            )}

            {draftHydrationReady && (
            <VariantMatrixEditor
              key={variantEditorKey}
              productSku={form.reference || null}
              productName={form.name || "Product"}
              defaultCost={num(form.costPrice)}
              defaultSelling={num(form.sellingPrice)}
              defaultWeightKg={num(form.weightKg)}
              defaultLengthCm={num(form.lengthCm)}
              defaultWidthCm={num(form.widthCm)}
              defaultHeightCm={num(form.heightCm)}
              defaultLocationId={defaultLocationId}
              initialPayload={variation}
              onChange={setVariation}
            />
            )}

            {variation.hasData && (
              <div className="border-t border-slate-100 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-slate-600">Opening stock (optional)</p>
                  <button type="button" onClick={() => { setStockChoice((c) => c === "record" ? "none" : "record"); if (openingStock.length === 0 && stockChoice !== "record") addOpeningStockRow(); }} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 whitespace-nowrap">
                    <i className={stockChoice === "record" ? "ri-checkbox-circle-line" : "ri-add-circle-line"} />
                  {stockChoice === "record" ? "Stock by location" : "Stock by location"}
                  </button>
                </div>
                {stockChoice === "record" && (
                  <div className="space-y-2">
                    {(data?.locations ?? []).length === 0 ? (
                      <div className="px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-700">
                        <i className="ri-map-pin-line mr-1" /> No locations yet — create one on the Locations page before stock can be placed.
                      </div>
                    ) : (
                      <>
                        {openingStock.map((row, i) => (
                          <div key={i} className="grid grid-cols-1 gap-2 items-center sm:grid-cols-[1fr_1fr_100px_110px_32px]">
                            <select value={row.variant_sku} onChange={(e) => updateOpeningStockRow(i, "variant_sku", e.target.value)} className={inputCls}>
                              <option value="">Select variant...</option>
                              {variation.variants.map((v) => (
                                <option key={v.sku} value={v.sku}>
                                  {v.sku}{v.option_values && Object.keys(v.option_values).length ? ` — ${Object.values(v.option_values).join(" / ")}` : ""}
                                </option>
                              ))}
                            </select>
                            <select value={row.location_id} onChange={(e) => updateOpeningStockRow(i, "location_id", e.target.value)} className={inputCls}>
                              <option value="">Select location...</option>
                              {(data?.locations ?? []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                            <input type="number" min="0" value={row.qty} onChange={(e) => updateOpeningStockRow(i, "qty", e.target.value)} className={inputCls} placeholder="Qty" />
                            <input type="number" step="0.01" min="0" value={row.unit_cost} onChange={(e) => updateOpeningStockRow(i, "unit_cost", e.target.value)} className={inputCls} placeholder="Unit cost" />
                            <button type="button" onClick={() => removeOpeningStockRow(i)} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500" aria-label="Remove row">
                              <i className="ri-close-line" />
                            </button>
                          </div>
                        ))}
                        <button type="button" onClick={addOpeningStockRow} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 whitespace-nowrap">
                          <i className="ri-add-line" /> Add another variant &amp; location
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
        </div>

        {/* STEP 2 — Bundle */}
        {step === 1 && form.productType === "bundle" && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700">
              <i className="ri-information-line mr-1" />
              A bundle is a product assembled from other sellable SKUs. Components are added in the Bundle tab on the product page after creation — this keeps a bundle from being faked as ordinary stock.
            </div>
            <p className="text-sm text-slate-600">You've already entered the name, SKU and selling price. That's everything needed to create the bundle — components come next.</p>
          </div>
        )}

        {/* STEP 0 continued — images, description, identifiers (optional groups) */}
        {step === 0 && org && (
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">More product details</p>
            <p className="text-sm text-slate-500">Optional groups stay collapsed. Add reusable catalogue facts now so later listings do not need to re-enter them. None of these block eBay or BigCommerce publication.</p>

            <details className="group border border-slate-200 rounded-lg" open>
              <summary className="flex items-center justify-between px-4 py-3 cursor-pointer list-none">
                <span className="text-sm font-medium text-slate-700">Identity</span>
                <i className="ri-arrow-down-s-line text-slate-400 transition-transform group-open:rotate-180" />
              </summary>
              <div className="px-4 pb-4 pt-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Brand <span className="text-slate-400 font-normal">(optional)</span></label>
                  <input value={form.brand} onChange={(e) => set("brand", e.target.value)} className={inputCls} placeholder="e.g. Acme" />
                  <p className="text-[11px] text-slate-400 mt-1">Enter the real brand. Recommended for later marketplace listings.</p>
                </div>
                <div>
                  <label className={labelCls}>Manufacturer</label>
                  <input value={form.manufacturer} onChange={(e) => set("manufacturer", e.target.value)} className={inputCls} placeholder="Leave blank if unknown" />
                  <p className="text-[11px] text-slate-400 mt-1">Do not enter N/A. Leave blank when there is no manufacturer.</p>
                </div>
                <div>
                  <label className={labelCls}>MPN / model number</label>
                  <input value={form.mpn} onChange={(e) => { set("mpn", e.target.value); set("modelNumber", e.target.value); }} className={inputCls} placeholder="Leave blank if unknown" />
                  {form.mpn.trim() && !sanitizeManufacturerIdentifier(form.mpn) && (
                    <p className="text-[11px] text-amber-700 mt-1">“{form.mpn}” is not a real MPN. It will be stored as blank.</p>
                  )}
                </div>
                <div>
                  <label className={labelCls}>Condition</label>
                  <select value={form.condition} onChange={(e) => set("condition", e.target.value)} className={inputCls}>
                    <option value="">Not set</option>
                    {CANONICAL_CONDITION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-400 mt-1">Tradivo condition. Channels map this themselves. Required to publish to eBay.</p>
                </div>
                <div>
                  <label className={labelCls}>Material</label>
                  <input value={form.material} onChange={(e) => set("material", e.target.value)} className={inputCls} placeholder="e.g. PVC, leather" />
                  <p className="text-[11px] text-slate-400 mt-1">Stored as a reusable product attribute. Leave blank if it does not apply.</p>
                </div>
                <div>
                  <label className={labelCls}>Product type</label>
                  <input value={form.productType} readOnly className={inputCls + " bg-slate-100"} />
                </div>
                <div>
                  <label className={labelCls}>Status</label>
                  <select value={form.status} onChange={(e) => set("status", e.target.value as "active" | "draft")} className={inputCls}>
                    <option value="active">Active</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
              </div>
            </details>

            <details className="group border border-slate-200 rounded-lg">
              <summary className="flex items-center justify-between px-4 py-3 cursor-pointer list-none">
                <span className="text-sm font-medium text-slate-700">Identifiers</span>
                <i className="ri-arrow-down-s-line text-slate-400 transition-transform group-open:rotate-180" />
              </summary>
              <div className="px-4 pb-4 pt-1 space-y-3">
                <p className="text-xs text-slate-500">
                  Enter a real GTIN, UPC or EAN if the product has one. Never invent a barcode, and do not type N/A, zeros, or dummy values.
                  Product-level codes belong here; variant-specific codes are added on the product page after creation.
                  “Product has no barcode” only means Tradivo has no retail identifier — it is not Amazon exemption.
                </p>
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={form.identifiersNotApplicable} onChange={(e) => set("identifiersNotApplicable", e.target.checked)} />
                  Product has no barcode / GTIN
                </label>
                {wizardIdentifierError() && (
                  <p className="text-xs text-red-600">{wizardIdentifierError()}</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>GTIN <span className="text-slate-400 font-normal">(optional)</span></label>
                    <input value={form.gtin} disabled={form.identifiersNotApplicable} onChange={(e) => set("gtin", e.target.value)} className={inputCls} placeholder="Real GTIN only" />
                  </div>
                  <div>
                    <label className={labelCls}>UPC</label>
                    <input value={form.upc} disabled={form.identifiersNotApplicable} onChange={(e) => set("upc", e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>EAN</label>
                    <input value={form.ean} disabled={form.identifiersNotApplicable} onChange={(e) => set("ean", e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Barcode</label>
                    <input value={form.barcode} onChange={(e) => set("barcode", e.target.value)} className={inputCls} placeholder="Internal scan code" />
                  </div>
                </div>
              </div>
            </details>

            <details className="group border border-slate-200 rounded-lg">
              <summary className="flex items-center justify-between px-4 py-3 cursor-pointer list-none">
                <span className="text-sm font-medium text-slate-700">Package / shipping measurements</span>
                <i className="ri-arrow-down-s-line text-slate-400 transition-transform group-open:rotate-180" />
              </summary>
              <div className="px-4 pb-4 pt-1 space-y-3">
                <p className="text-xs text-slate-500">These are package (shipping) measurements used to pre-fill parcels. They are not a separate item-size record. Leave blank when unknown — do not guess.</p>
                {form.productType === "variation" && (
                  <p className="text-xs text-slate-500">Product-level defaults. Variant rows override only when a SKU ships differently.</p>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div><label className={labelCls}>Package weight (kg)</label><input type="number" step="0.01" min="0" value={form.weightKg} onChange={(e) => set("weightKg", e.target.value)} className={inputCls} placeholder="0.5" /></div>
                  <div><label className={labelCls}>Package length (cm)</label><input type="number" step="0.1" min="0" value={form.lengthCm} onChange={(e) => set("lengthCm", e.target.value)} className={inputCls} placeholder="20" /></div>
                  <div><label className={labelCls}>Package width (cm)</label><input type="number" step="0.1" min="0" value={form.widthCm} onChange={(e) => set("widthCm", e.target.value)} className={inputCls} placeholder="15" /></div>
                  <div><label className={labelCls}>Package height (cm)</label><input type="number" step="0.1" min="0" value={form.heightCm} onChange={(e) => set("heightCm", e.target.value)} className={inputCls} placeholder="10" /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} htmlFor="unit-of-measure">Unit of measure</label>
                    <input id="unit-of-measure" value={form.unit} onChange={(e) => set("unit", e.target.value)} className={inputCls} placeholder="each" />
                  </div>
                  <div>
                    <label className={labelCls}>Country / region of manufacture</label>
                    <input value={form.countryOfOrigin} onChange={(e) => set("countryOfOrigin", e.target.value)} className={inputCls} placeholder="AU" />
                  </div>
                </div>
              </div>
            </details>

            <details className="group border border-slate-200 rounded-lg">
              <summary className="flex items-center justify-between px-4 py-3 cursor-pointer list-none">
                <span className="text-sm font-medium text-slate-700">Content &amp; SEO</span>
                <i className="ri-arrow-down-s-line text-slate-400 transition-transform group-open:rotate-180" />
              </summary>
              <div className="px-4 pb-4 pt-1 space-y-4">
                <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-xs text-slate-600">
                      <i className="ri-sparkling-line mr-1 text-emerald-600" />
                      Let AI draft your content from the details you've entered.
                    </p>
                    <button
                      type="button"
                      onClick={handleEnrich}
                      disabled={enriching || form.name.trim().length === 0}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-medium rounded-md whitespace-nowrap flex items-center gap-1.5"
                    >
                      <i className={enriching ? "ri-loader-4-line animate-spin" : "ri-magic-line"} />
                      {enriching ? "Drafting..." : "Generate with AI"}
                    </button>
                  </div>

                  {enrichError && <p className="mt-2 text-xs text-red-600">{enrichError}</p>}

                  {enrichDraft && (
                    <div className="mt-3 space-y-3">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          {enrichmentDraftCaption(enrichDraft)}
                        </p>
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={() => applyEnrichDraft(false)} className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-md whitespace-nowrap">
                            Apply to fields
                          </button>
                          <button type="button" onClick={discardEnrichDraft} className="px-2.5 py-1 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs font-medium rounded-md whitespace-nowrap">
                            Discard
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2 text-xs text-slate-700">
                        <div>
                          <p className="font-medium text-slate-500">Description</p>
                          <p className="whitespace-pre-wrap">{enrichDraft.description}</p>
                        </div>
                        <div>
                          <p className="font-medium text-slate-500">Short description</p>
                          <p>{enrichDraft.shortDescription || "—"}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <div>
                            <p className="font-medium text-slate-500">SEO title</p>
                            <p>{enrichDraft.seoTitle || "—"}</p>
                          </div>
                          <div>
                            <p className="font-medium text-slate-500">Meta description</p>
                            <p>{enrichDraft.metaDescription || "—"}</p>
                          </div>
                          <div>
                            <p className="font-medium text-slate-500">SEO keywords</p>
                            <p>{enrichDraft.seoKeywords || enrichDraft.tags.join(", ") || "—"}</p>
                          </div>
                        </div>
                        {enrichDraft.tags.length > 0 && (
                          <div>
                            <p className="font-medium text-slate-500">Suggested tags</p>
                            <div className="flex flex-wrap gap-1.5 mt-1">
                              {enrichDraft.tags.map((t) => (
                                <span key={t} className="px-2 py-0.5 bg-white border border-slate-200 rounded-full text-[11px] text-slate-600">{t}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className={labelCls} htmlFor="description">Description</label>
                  <textarea id="description" value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} rows={4} maxLength={500} placeholder="Full marketing description..." />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Short description</label>
                    <textarea value={form.shortDescription} onChange={(e) => set("shortDescription", e.target.value)} className={inputCls} rows={2} maxLength={200} placeholder="One-line summary for listings..." />
                  </div>
                  <div>
                    <label className={labelCls}>Tags (comma separated)</label>
                    <input value={form.tags} onChange={(e) => set("tags", e.target.value)} className={inputCls} placeholder="summer, unisex, cotton" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>SEO title</label>
                    <input value={form.seoTitle} onChange={(e) => set("seoTitle", e.target.value)} className={inputCls} maxLength={60} placeholder="Keyword-rich page title" />
                  </div>
                  <div>
                    <label className={labelCls}>Meta description</label>
                    <input value={form.metaDescription} onChange={(e) => set("metaDescription", e.target.value)} className={inputCls} maxLength={160} placeholder="Search snippet" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>SEO keywords</label>
                  <input value={form.seoKeywords} onChange={(e) => set("seoKeywords", e.target.value)} className={inputCls} placeholder="keyword, keyword" />
                </div>
                <div>
                  <label className={labelCls}>URL slug</label>
                  <input
                    value={displayedUrlSlug(form)}
                    onChange={(e) => setForm((f) => ({ ...f, urlSlug: e.target.value, slugEdited: true }))}
                    className={inputCls}
                    placeholder="from the product name"
                  />
                  <p className="text-xs text-slate-400 mt-1">Follows the product name until you edit it.</p>
                </div>
              </div>
            </details>

            {/* Media */}
            <details className="group border border-slate-200 rounded-lg" open={draftMedia.length > 0}>
              <summary className="flex items-center justify-between px-4 py-3 cursor-pointer list-none">
                <span className="text-sm font-medium text-slate-700">Images {draftMedia.length > 0 && <span className="text-emerald-600">({draftMedia.length})</span>}</span>
                <i className="ri-arrow-down-s-line text-slate-400 transition-transform group-open:rotate-180" />
              </summary>
              <div className="px-4 pb-4 pt-1 space-y-4">
                <p className="text-sm text-slate-600">Add several images if you have them. Order is kept. Mark one as primary. Variant-specific images can be set on the product page. Draft images transfer onto the product without duplicating files.</p>
                <p className="text-xs text-slate-500">Marketplace-quality photos: clear product on a plain background, in focus, no watermarks. Extra channel-specific image rules are not applied here.</p>
                <p className="text-xs text-slate-500">Channel-specific primary images are chosen on the Selling step. Each account picks from this same library — the catalogue primary image never changes.</p>
                {mediaNotice && (
                  <div className="px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-800">
                    <i className="ri-information-line mr-1" />
                    {mediaNotice}
                  </div>
                )}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors"
                >
                  <i className="ri-upload-cloud-2-line text-3xl text-slate-400 mb-2 block" />
                  <p className="text-sm text-slate-600 font-medium">{mediaBusy ? "Saving images..." : "Drag images here or click to browse"}</p>
                  <p className="text-xs text-slate-400 mt-1">JPEG, PNG, WebP · Max 10 MB each · Saved with the draft</p>
                  <input ref={fileInputRef} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleFiles(e.target.files)} />
                </div>
                {draftMedia.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">                    {draftMedia.map((item) => (
                      <div key={item.id} className="relative border border-slate-200 rounded-lg overflow-hidden group">
                        {item.src ? (
                          <img src={item.src} alt={item.filename} className="w-full aspect-square object-cover" />
                        ) : (
                          <div className="w-full aspect-square bg-slate-100 flex items-center justify-center text-xs text-slate-400">Saved</div>
                        )}
                        {item.isPrimary && <span className="absolute top-1 left-1 text-[10px] font-semibold bg-emerald-600 text-white px-1.5 py-0.5 rounded-full">Primary</span>}
                        {!item.isPrimary && (
                          <button
                            type="button"
                            onClick={() => setPrimaryDraftImage(item.mediaAssetId)}
                            className="absolute bottom-7 left-1 text-[10px] font-medium bg-white/90 text-slate-700 px-1.5 py-0.5 rounded"
                          >
                            Set primary
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeDraftImage(item.id)}
                          className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center rounded-full bg-slate-900/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label={`Remove ${item.filename}`}
                        >
                          <i className="ri-close-line text-xs" />
                        </button>
                        <p className="p-1.5 text-[10px] text-slate-500 truncate">{item.filename}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </details>

            <details className="group border border-slate-200 rounded-lg">
              <summary className="flex items-center justify-between px-4 py-3 cursor-pointer list-none">
                <span className="text-sm font-medium text-slate-700">Supplier &amp; tax</span>
                <i className="ri-arrow-down-s-line text-slate-400 transition-transform group-open:rotate-180" />
              </summary>
              <div className="px-4 pb-4 pt-1 space-y-3">
                <p className="text-xs text-slate-500">Supplier is chosen on Essentials. Tax profile is optional catalogue data.</p>
                <div>
                  <label className={labelCls}>Tax profile</label>
                  <input value={form.taxProfile} onChange={(e) => set("taxProfile", e.target.value)} className={inputCls} placeholder="e.g. Standard" />
                </div>
              </div>
            </details>
          </div>
        )}

        {/* STEP 2 — Selling */}
        {step === 2 && org && (
          <WizardSellingStep
            orgId={org.id}
            intents={sellingIntents}
            snapshot={snapshotFromWizard({
              name: form.name,
              sku: form.sku,
              brand: form.brand,
              manufacturer: form.manufacturer,
              mpn: form.mpn,
              condition: form.condition,
              categoryId: form.categoryId,
              gtin: form.gtin,
              upc: form.upc,
              ean: form.ean,
              barcode: form.barcode,
              identifiersNotApplicable: form.identifiersNotApplicable,
              weightKg: form.weightKg,
              lengthCm: form.lengthCm,
              widthCm: form.widthCm,
              heightCm: form.heightCm,
              description: form.description,
              shortDescription: form.shortDescription,
              sellingPrice: form.sellingPrice,
              supplierId: form.supplier.supplierId,
              countryOfOrigin: form.countryOfOrigin,
              material: form.material,
            }, draftMedia.length)}
            condition={form.condition}
            description={form.description}
            onIntents={setSellingIntents}
            onCondition={(value) => set("condition", value)}
            onDescription={(value) => set("description", value)}
            onJumpToProduct={() => setStep(0)}
            productPrice={num(form.sellingPrice)}
            media={wizardMediaOptions}
            variants={form.productType === "variation"
              ? variation.variants.map((variant) => ({ sku: variant.sku, sellingPrice: variant.selling_price }))
              : []}
          />
        )}

        {/* STEP 3 — Review */}
        {step === 3 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Here's what will be created. Creation is transactional — either the full product and its SKUs are created, or nothing is.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="border border-slate-200 rounded-lg p-4">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Product</h3>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-slate-500">Name</dt><dd className="text-slate-800 font-medium">{form.name || "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Type</dt><dd className="text-slate-800">{form.productType === "simple" ? "Simple" : form.productType === "variation" ? "Variation" : "Bundle"}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">SKUs</dt><dd className="text-slate-800 font-semibold">{form.productType === "variation" ? `${variation.combinationCount} prepared` : "1"}{form.productType === "variation" ? ` · ${variation.duplicateSkus.length} conflict${variation.duplicateSkus.length === 1 ? "" : "s"}` : ""}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Category</dt><dd className="text-slate-800">{categoryName ?? "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Supplier</dt><dd className="text-slate-800">{supplierName ?? "—"}{form.supplier.supplierSku ? <span className="font-mono text-slate-400 ml-1">({form.supplier.supplierSku})</span> : null}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Selling price</dt><dd className="text-slate-800">{formatMoneyPreview(num(form.sellingPrice), "A$")}</dd></div>
                </dl>
              </div>

              <div className="border border-slate-200 rounded-lg p-4">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Stock</h3>
                {form.productType === "variation" && (
                  <p className="text-sm text-slate-600 mb-2" data-testid="matrix-location-review">
                    {matrixLocationName
                      ? `Matrix quantities use ${matrixLocationName}.`
                      : "Matrix quantities are not placed until you choose a location."}
                  </p>
                )}
                {openingQtyWarnings.length > 0 && (
                  <p className="mb-2 text-sm text-red-600">{openingQtyWarnings[0]}</p>
                )}
                {form.productType === "bundle" ? (
                  <p className="text-sm text-slate-600">Bundles don't hold stock directly — availability comes from their components.</p>
                ) : projectedStock.length === 0 ? (
                  <div className="flex items-start gap-2 text-sm text-slate-600">
                    <i className="ri-inbox-line text-slate-400 mt-0.5" />
                    <span>No opening stock. This product starts at zero — count it in later or place a purchase order.</span>
                  </div>
                ) : (
                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Total</dt>
                      <dd className="text-slate-800 font-semibold">{projectedStockTotal} units</dd>
                    </div>
                    {projectedStock.map((r, i) => (
                      <div key={i} className="flex justify-between">
                        <dt className="text-slate-500">
                          {(data?.locations ?? []).find((l) => l.id === r.location_id)?.name ?? "Location"}
                          {r.sku && <span className="font-mono text-slate-400 ml-1">({r.sku})</span>}
                        </dt>
                        <dd className="text-slate-800 font-semibold">{r.qty} units</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            </div>

            {/* Warnings */}
            <div className="space-y-2">
              {form.productType === "variation" && variation.duplicateSkus.length > 0 && (
                <div className="px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700">
                  <i className="ri-error-warning-line mr-1" /> Duplicate SKUs detected. Go back to Options to fix them.
                </div>
              )}
              {!form.description && (
                <div className="px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-700">
                  <i className="ri-information-line mr-1" /> No description set. You can add one later, but a description helps listings and SEO.
                </div>
              )}
            </div>

            {(() => {
              const snap = snapshotFromWizard({
                name: form.name,
                sku: form.sku,
                brand: form.brand,
                manufacturer: form.manufacturer,
                mpn: form.mpn,
                condition: form.condition,
                categoryId: form.categoryId,
                gtin: form.gtin,
                upc: form.upc,
                ean: form.ean,
                barcode: form.barcode,
                identifiersNotApplicable: form.identifiersNotApplicable,
                weightKg: form.weightKg,
                lengthCm: form.lengthCm,
                widthCm: form.widthCm,
                heightCm: form.heightCm,
                description: form.description,
                shortDescription: form.shortDescription,
                sellingPrice: form.sellingPrice,
                supplierId: form.supplier.supplierId,
                countryOfOrigin: form.countryOfOrigin,
                material: form.material,
              }, draftMedia.length);
              const rows = evaluateWizardSelling(sellingIntents, snap);
              const counts = selectedReadyCount(rows);
              const required = uniqueRequiredFixes(rows);
              const recommended = uniqueRecommendedFixes(rows, snap);
              const stockQty = projectedStockTotal;
                  const details = evaluateProductDetailsPreparation({
                    name: form.name,
                    brand: form.brand,
                    gtin: form.gtin,
                    upc: form.upc,
                    ean: form.ean,
                    identifiersNotApplicable: form.identifiersNotApplicable,
                    condition: form.condition,
                    mpn: form.mpn,
                    weightKg: num(form.weightKg),
                    lengthCm: num(form.lengthCm),
                    widthCm: num(form.widthCm),
                    heightCm: num(form.heightCm),
                    description: form.description,
                    imageCount: draftMedia.length,
                  });
                  return (
                <div className="border border-slate-200 rounded-lg p-4 space-y-3" data-testid="wizard-readiness">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-testid="channel-publish-readiness">Channel publish readiness</h3>
                  <p className="text-sm text-slate-700" data-testid="product-details-prep">{details.label}</p>
                  <p className="text-xs text-slate-500" data-testid="amazon-catalogue-status">{formatAmazonCatalogueStatus(details)}</p>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    <div><dt className="text-slate-500">Product</dt><dd className="font-medium text-slate-800">{form.name || "—"}</dd></div>
                    <div><dt className="text-slate-500">Variants</dt><dd className="font-medium text-slate-800">{form.productType === "variation" ? variation.variants.length : 1}</dd></div>
                    <div><dt className="text-slate-500">Images</dt><dd className="font-medium text-slate-800">{draftMedia.length}</dd></div>
                    <div><dt className="text-slate-500">Stock</dt><dd className="font-medium text-slate-800">{stockQty} units</dd></div>
                  </dl>
                  {counts.selected === 0 ? (
                    <p className="text-sm text-slate-600">No channels selected. Create the product in Tradivo, then publish later.</p>
                  ) : (
                    <ul className="space-y-2">
                      {sellingIntents.filter((intent) => intent.selected).map((intent) => {
                        const readiness = rows.find((row) => row.connectorId === intent.connectorId);
                        const quote = resolveWizardChannelPrice({
                          productPrice: num(form.sellingPrice),
                          variants: form.productType === "variation"
                            ? variation.variants.map((variant) => ({ sku: variant.sku, sellingPrice: variant.selling_price }))
                            : [],
                          connectorDefault: intent.connectorPricing ?? { strategy: "inherit", config: {} },
                          productOverride: intent.priceOverride ?? null,
                        });
                        const priceError = (intent.priceOverride
                          ? wizardPriceAmountError(intent.priceOverride.strategy, intent.priceOverride.amount)
                          : null) ?? wizardQuotePriceError(quote);
                        const source = quote.source === "product_override" ? "product override" : quote.source === "connector_default" ? "connector default" : "catalogue base";
                        const channelImage = resolveWizardChannelPrimaryMedia({
                          chosenMediaAssetId: intent.primaryMediaAssetId,
                          gallery: wizardMediaOptions,
                        });
                        return (
                          <li key={intent.connectorId} className={`text-sm ${priceError ? "text-red-700" : readiness?.status === "ready" ? "text-emerald-700" : "text-amber-700"}`}>
                            {readiness?.compact}
                            {priceError ? (
                              <span className="block text-xs text-red-600">{priceError}</span>
                            ) : (
                              <span className="block text-xs text-slate-600">
                                {formatWizardPriceRange(quote.min, quote.max)} · {source}
                              </span>
                            )}
                            <span className="block text-xs text-slate-500" data-testid={`channel-listing-config-${intent.connectorId}`}>
                              {wizardListingConfigSummary({
                                provider: intent.provider,
                                variantCount: form.productType === "variation" ? variation.variants.length : 1,
                                channelCategoryName: intent.channelCategoryName,
                              })}
                            </span>
                            <span
                              className={`block text-xs ${channelImage.authority === "missing_override" ? "text-amber-700" : "text-slate-600"}`}
                              data-testid={`channel-primary-image-${intent.connectorId}`}
                            >
                              {channelImage.label}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {required.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Required to publish</p>
                      <ul className="mt-1 space-y-1">
                        {required.map((r) => <li key={r.key} className="text-sm text-amber-700">• {r.label}</li>)}
                      </ul>
                      <button type="button" onClick={() => setStep(2)} className="mt-2 text-xs font-medium text-emerald-700">Fix on Selling</button>
                    </div>
                  )}
                  {recommended.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recommended</p>
                      <ul className="mt-1 space-y-1">
                        {recommended.map((r) => <li key={r.key} className="text-xs text-slate-500">• {r.label}</li>)}
                      </ul>
                    </div>
                  )}
                  <p className="text-xs text-slate-400">Create Product saves Tradivo only. Publishing to a channel stays a separate, explicit action.</p>
                </div>
              );
            })()}
          </div>
        )}

        {/* Why Continue is blocked: entered quantities with nowhere to land. */}
        {step === 1 && form.productType === "variation" && (() => {
          const matrixProblem = matrixLocationProblem({
            productType: form.productType,
            defaultLocationId,
            variants: variation.variants,
          });
          return matrixProblem ? (
            <p className="mt-4 px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-800" role="status" data-testid="matrix-location-block">
              <i className="ri-error-warning-line mr-1" />{matrixProblem}
            </p>
          ) : null;
        })()}

        {/* Footer nav */}
        <div className="mt-8 pt-5 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={back} disabled={step === 0 || saving} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-40 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-1">
              <i className="ri-arrow-left-line" /> Back
            </button>
          </div>
          {step < steps.length - 1 ? (
            <button onClick={next} disabled={!canProceed() || mediaBusy || saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-1">
              {step === 2 ? "Continue to review" : "Continue"} <i className="ri-arrow-right-line" />
            </button>
          ) : (
            <button onClick={handleCreate} disabled={saving || discardingDraft || mediaBusy || recoveryUnreadable} title={recoveryUnreadable ? "This draft's earlier create cannot be established, so creating again is blocked for it" : undefined} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
              <i className={saving ? "ri-loader-4-line animate-spin" : "ri-check-line"} />
              {saving ? "Creating..." : "Create Product"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
